/**
 * Ukrainian UI strings. The interface must be fully available in Ukrainian and shown by default
 * (Закон «Про забезпечення функціонування української мови як державної», ст. 27).
 * Plurals use i18next suffixes: _one (1, 21), _few (2–4, 22–24), _many (5–20, 25…), _other (fractions).
 */
export const uk = {
  common: {
    appName: 'Parta',
    loading: 'Завантаження…',
    retry: 'Спробувати ще раз',
    save: 'Зберегти',
    cancel: 'Скасувати',
    back: 'Назад',
    continue: 'Продовжити',
    signOut: 'Вийти',
    error: 'Щось пішло не так. Спробуйте ще раз.',
    offline: 'Немає зʼєднання. Показуємо збережені дані.',
    updatedAt: 'Оновлено о {{time}}',
    demoBadge: 'ДЕМО',
    readAloud: 'Прослухати',
    stopReading: 'Зупинити',
    close: 'Закрити',
  },
  // the preview's switcher of the looks being tried (design v4)
  design: {
    variant: 'Варіант',
    looks: {
      sky: 'Небо',
      warm: 'Тепло',
      fresh: 'Свіжість',
      glass: 'Скло',
      glass2: 'Скло 2',
      shapes: 'Форми',
      shapes2: 'Форми 2',
    },
    age: 'Вік',
    junior: 'Молодші',
    teen: 'Старші',
  },
  profile: {
    title: 'Профіль',
    whose: 'Чий профіль показувати',
    signedInAs: 'Ви увійшли як',
    version: 'Версія {{version}}',
  },
  setup: {
    title: 'Застосунок ще не підключено до сервера',
    body: 'Додайте адресу проєкту Supabase та публічний ключ у файл .env.local (див. .env.example).',
  },
  auth: {
    signInTitle: 'Вхід',
    signInSubtitle:
      'Обліковий запис створює школа. Якщо у вас є код запрошення — скористайтеся ним.',
    email: 'Електронна пошта',
    password: 'Пароль',
    signIn: 'Увійти',
    signingIn: 'Входимо…',
    invalidCredentials: 'Неправильна пошта або пароль.',
    tooManyAttempts: 'Забагато спроб. Зачекайте кілька хвилин.',
    haveInvite: 'Маю код запрошення',
    mfaTitle: 'Двофакторна перевірка',
    mfaRequired: 'Для працівників школи вхід можливий лише з двофакторною перевіркою.',
    mfaEnrollTitle: 'Підключіть застосунок-автентифікатор',
    mfaEnrollHint:
      'Відскануйте QR-код у Google Authenticator, Microsoft Authenticator або іншому застосунку й введіть 6-значний код.',
    mfaSecretHint: 'Не вдається відсканувати? Введіть ключ вручну: {{secret}}',
    mfaCode: 'Код із застосунку',
    mfaVerify: 'Підтвердити',
    mfaInvalidCode: 'Код не підійшов. Перевірте час на телефоні й спробуйте новий код.',
    mfaPhoneHint:
      'Додайте ключ у застосунок-автентифікатор на цьому телефоні (кнопка нижче) і введіть 6-значний код.',
    mfaOpenApp: 'Відкрити в застосунку-автентифікаторі',
    demoTitle: 'Демо: увійти як',
    demoPersonas: {
      director: 'Директор',
      teacher: 'Вчитель 7-Б',
      parent: 'Мама (2-Б і 7-Б)',
      student: 'Учень 7-Б',
      student2: 'Учениця 7-Б',
      psychologist: 'Психолог',
      senior: '11 клас (НМТ)',
      abroad: 'З-за кордону (7-Б)',
    },
  },
  invite: {
    title: 'Код запрошення',
    subtitle: 'Код видає школа: на папірці, у повідомленні або QR-кодом.',
    code: 'Код запрошення',
    check: 'Перевірити код',
    checking: 'Перевіряємо…',
    school: 'Школа',
    role: 'Роль',
    createTitle: 'Створіть обліковий запис',
    passwordHint: 'Щонайменше 12 символів. Найкраще — кілька слів, які легко запамʼятати.',
    passwordRepeat: 'Повторіть пароль',
    passwordMismatch: 'Паролі не збігаються.',
    create: 'Створити обліковий запис',
    creating: 'Створюємо…',
    signedInAs: 'Ви увійшли як {{email}}. Запрошення буде додано до цього облікового запису.',
    attach: 'Додати до мого облікового запису',
    attaching: 'Додаємо…',
    otherCode: 'Ввести інший код',
    haveAccount: 'Уже маєте обліковий запис? Увійти',
    errors: {
      invalid_code:
        'Код не підійшов: він неправильний, уже використаний або протермінований. Перевірте його або попросіть у школи новий.',
      invalid_email: 'Перевірте адресу електронної пошти.',
      password_too_short: 'Пароль має містити щонайменше 12 символів.',
      password_too_long: 'Пароль задовгий. Скоротіть його.',
      weak_password: 'Цей пароль ненадійний. Оберіть інший.',
      email_taken:
        'Для цієї пошти вже є обліковий запис. Увійдіть у нього й відкрийте «Маю код запрошення» ще раз — код буде додано до вашого запису.',
      already_linked: 'Ваш обліковий запис уже приєднано до цієї школи в такій ролі.',
      invalid_session: 'Сесія завершилася. Увійдіть знову.',
      rate_limited: 'Забагато невдалих спроб. Спробуйте приблизно через 15 хвилин.',
    },
  },
  nav: {
    home: 'Головна',
    schedule: 'Розклад',
    journal: 'Журнал',
    grades: 'Оцінки',
    homework: 'Домашні завдання',
    homeworkShort: 'Завдання',
    announcements: 'Оголошення',
    notifications: 'Сповіщення',
    alarm: 'Тривога',
    messages: 'Повідомлення',
    chats: 'Чати',
    requests: 'Заяви',
    meetings: 'Зустрічі',
    admin: 'Адміністрування',
    menu: 'Меню',
    profile: 'Профіль',
    canteen: 'Харчування',
    events: 'Події',
    polls: 'Опитування',
    schoolLife: 'Життя школи',
    achievements: 'Досягнення',
    materials: 'Матеріали',
    album: 'Фотоальбом',
    quizzes: 'Тести',
    care: 'Турбота',
    lab: 'Лабораторія',
    clubs: 'Гуртки',
    tree: 'Дерево знань',
    nmt: 'НМТ',
    planning: 'План уроків',
    season: 'Сезон класу',
    yearbook: 'Альбом року',
    future: 'Моє майбутнє',
    abroad: 'З-за кордону',
    abroadSchool: 'Учні за кордоном',
    forms: 'Згоди й форми',
    rewards: 'Магазин нагород',
    moving: 'Переїзд без втрат',
    letters: 'Лист у майбутнє',
    life: 'Шкільне життя',
    settings: 'Налаштування',
    groups: {
      main: 'Головне меню',
      study: 'Навчання',
      talk: 'Спілкування',
      life: 'Шкільне життя',
      school: 'Школа',
      lessons: 'Уроки',
      class: 'Клас і школа',
    },
  },
  // the frame of every page (design v4): the bar on top and the search of sections
  shell: {
    search: 'Пошук розділу',
    searchPlaceholder: 'Знайти розділ…',
    nothingFound: 'Такого розділу немає',
    whereAmI: 'Де ви зараз',
    sections: 'Розділи',
    account: 'Акаунт',
  },
  life: {
    hint: 'Розділи, які відкриваєш час від часу.',
    class: 'Клас {{class}}',
  },
  context: {
    switch: 'Змінити профіль',
    guardian: 'Батьки · {{name}}, {{class}}',
    student: 'Учень {{class}}',
    teacher: 'Вчитель',
    homeroom: 'Класний керівник {{class}}',
    admin: 'Адміністрація',
    psychologist: 'Психолог',
    none: 'Вас ще не додано до жодної школи. Зверніться до адміністрації школи.',
  },
  format: {
    in_person: 'Очно',
    online: 'Онлайн',
    shelter: 'В укритті',
  },
  schedule: {
    test: 'Контрольна',
    title: 'Розклад',
    week: 'Тиждень {{from}} – {{to}}',
    thisWeek: 'Цей тиждень',
    prevWeek: 'Попередній тиждень',
    nextWeek: 'Наступний тиждень',
    today: 'Сьогодні',
    noLessons: 'Уроків немає',
    class: 'Клас',
    period: '{{n}} урок',
    room: 'каб. {{room}}',
    substitute: 'Заміна: {{name}}',
    substituting: 'Ви замінюєте',
    cancelled: 'Скасовано',
    views: { month: 'Місяць', week: 'Тиждень', day: 'День' },
    all: 'Усі',
    allDay: 'увесь день',
    chipsBack: 'Попередні предмети',
    chipsMore: 'Ще предмети',
    event: 'Подія',
    more_one: 'ще {{count}}',
    more_few: 'ще {{count}}',
    more_many: 'ще {{count}}',
    more_other: 'ще {{count}}',
    prev: { month: 'Попередній місяць', week: 'Попередній тиждень', day: 'Попередній день' },
    next: { month: 'Наступний місяць', week: 'Наступний тиждень', day: 'Наступний день' },
    manage: {
      title: 'Керування уроком',
      format: 'Формат уроку',
      cancel: 'Скасувати урок',
      restore: 'Повернути урок',
      substitute: 'Хто веде урок',
      noSubstitute: 'Вчитель за розкладом',
      close: 'Закрити',
    },
  },
  journal: {
    isTest: 'Контрольна робота',
    testsTooMany:
      'У {{class}} цього дня вже {{count}} контрольні ({{subjects}}). Радимо не більше двох на день — краще оберіть інший день.',
    testsOne: 'Цього дня в {{class}} вже є контрольна: {{subjects}}.',
    bring: 'Що принести на урок',
    bringHint: 'Наприклад: лінійка, циркуль. Учні побачать це в «Рюкзаку на завтра».',
    announcedAbsence: 'заява',
    title: 'Журнал',
    course: 'Предмет і клас',
    term: '{{n}} семестр',
    student: 'Учень',
    topicPlaceholder: 'Тема не вказана',
    editTopic: 'Тема уроку',
    thematic: 'Тематичні',
    average: 'Сер.',
    semester: 'Семестр',
    suggestion: 'Підказка системи: {{value}} (середнє {{raw}})',
    noSuggestion: 'Підказки немає — оцінку ставить учитель',
    empty: 'У цьому семестрі ще немає уроків.',
    noCourses: 'Вам ще не призначено предметів.',
    myCourses: 'Мої предмети',
    classCourses: 'Предмети класу {{class}}',
    kinds: {
      current: 'Поточна',
      test: 'Контрольна',
      thematic: 'Тематична',
      semester: 'Семестрова',
      year: 'Річна',
    },
    absence: {
      absent: 'н',
      alarm: 'тр',
      technical: 'тп',
    },
    absenceFull: {
      absent: 'Відсутній',
      alarm: 'Через тривогу',
      technical: 'Технічні причини',
    },
    special: {
      not_assessed: 'н/а',
      exempt: 'зв',
    },
    specialFull: {
      not_assessed: 'не атестований',
      exempt: 'звільнений',
    },
    editor: {
      grade: 'Оцінка',
      kind: 'Вид оцінки',
      attendance: 'Присутність',
      present: 'Присутній',
      clear: 'Прибрати оцінку',
      comment: 'Коментар (необовʼязково)',
      save: 'Зберегти',
      formative: 'Відгук',
    },
    legend: 'н — відсутній, тр — через тривогу, тп — технічні причини',
    lessonJournal: 'Журнал уроку',
    offline: {
      note: 'Немає зв’язку: оцінки й відмітки зберігаються на пристрої та підуть, щойно зв’язок повернеться.',
      waiting: 'Чекає зв’язку',
      waitingCount_one: '{{count}} зміна чекає зв’язку',
      waitingCount_few: '{{count}} зміни чекають зв’язку',
      waitingCount_many: '{{count}} змін чекають зв’язку',
      waitingCount_other: '{{count}} зміни чекають зв’язку',
      conflicts: 'Поки ви були без зв’язку, ці клітинки змінив хтось інший — що залишити?',
      mine: 'ваше: {{value}}',
      theirs: 'зараз у журналі: {{value}}',
      keepMine: 'Залишити моє',
      keepTheirs: 'Залишити як є',
      empty: 'порожньо',
      present: 'присутній',
    },
  },
  grades: {
    title: 'Оцінки',
    average: 'Середній бал',
    noGrades: 'Оцінок поки немає.',
    absences: 'Пропуски',
    absencesCount_one: '{{count}} пропуск',
    absencesCount_few: '{{count}} пропуски',
    absencesCount_many: '{{count}} пропусків',
    absencesCount_other: '{{count}} пропуску',
    formative: 'Відгуки вчителів',
    latest: 'Нові оцінки',
    bySubject: 'За предметами',
    noAbsences: 'Пропусків немає.',
  },
  homework: {
    files: {
      photo: 'Фото',
      gallery: 'З галереї',
      voice: 'Записати голос',
      stop: 'Зупинити',
      uploading: 'Завантажуємо…',
      remove: 'Прибрати файл',
      removeHint: 'Щоб прибрати файл, утримуй його.',
      failed: 'Не вдалося завантажити файл. Спробуй ще раз.',
      noMicrophone: 'Немає доступу до мікрофона.',
      offline:
        'Фото й записи додаються, коли є звʼязок. Текст відповіді можна надіслати й без нього.',
      onlyFiles: 'Відповідь у файлах',
      openInBrowser: 'Відкрити',
    },
    criteriaLabel: 'За що оцінка (необовʼязково)',
    criteriaHint:
      'Кожен критерій з нового рядка, наприклад:\n2 бали — розвʼязання\n1 бал — висновок\n1 бал — акуратність',
    criteria: 'За що оцінка',
    title: 'Домашні завдання',
    due: 'Здати до {{date}}',
    dueToday: 'Здати сьогодні',
    overdue: 'Термін минув {{date}}',
    noHomework: 'Домашніх завдань немає.',
    upcoming: 'Найближчі',
    past: 'Минулі',
    status: {
      none: 'Не здано',
      submitted: 'Здано, чекає перевірки',
      returned: 'Повернуто на доопрацювання',
      accepted: 'Зараховано',
      notRequired: 'Без здачі',
      queued: 'Очікує звʼязку',
    },
    answer: 'Відповідь',
    answerPlaceholder: 'Напишіть відповідь або коротко опишіть, що зроблено',
    submit: 'Здати',
    resubmit: 'Здати повторно',
    submitting: 'Надсилаємо…',
    feedback: 'Відгук учителя',
    onlyStudent: 'Здати завдання може лише учень зі свого облікового запису.',
    create: 'Нове завдання',
    titleLabel: 'Завдання',
    bodyLabel: 'Пояснення (необовʼязково)',
    dueLabel: 'Термін',
    requiresSubmission: 'Учні здають відповідь у застосунку',
    publish: 'Задати',
    answers: 'Відповідей: {{count}}',
    toReview: 'Чекають перевірки: {{count}}',
    accept: 'Зарахувати',
    return: 'Повернути',
    feedbackPlaceholder: 'Коментар для учня',
    notHandedIn: 'Не здали',
  },
  announcements: {
    title: 'Оголошення',
    new: 'Нове оголошення',
    audience: 'Кому',
    remind_one: 'Нагадати {{count}} непрочитавшому',
    remind_few: 'Нагадати {{count}} непрочитавшим',
    remind_many: 'Нагадати {{count}} непрочитавшим',
    remind_other: 'Нагадати {{count}} непрочитавшим',
    reminded: 'Нагадування надіслано ({{count}})',
    remindedRecently: 'Нагадування вже надсилали протягом 6 годин',
    audiences: {
      school: 'Усій школі',
      staff: 'Працівникам',
      parents: 'Усім батькам',
      class: 'Класу та батькам',
      class_parents: 'Батькам класу',
    },
    titleLabel: 'Заголовок',
    bodyLabel: 'Текст',
    requiresAck: 'Попросити підтвердити ознайомлення',
    publish: 'Опублікувати',
    publishing: 'Публікуємо…',
    read: 'Прочитали {{read}} з {{total}}',
    ack: 'Ознайомлений(а)',
    acknowledged: 'Ви ознайомились',
    markRead: 'Позначити прочитаним',
    empty: 'Оголошень поки немає.',
    from: 'Від: {{author}}',
    earlier: 'Раніше',
  },
  notifications: {
    title: 'Сповіщення',
    empty: 'Нових сповіщень немає.',
    markAll: 'Позначити всі прочитаними',
    unread_one: '{{count}} нове',
    unread_few: '{{count}} нові',
    unread_many: '{{count}} нових',
    unread_other: '{{count}} нових',
  },
  classesNow: {
    title: 'Класи зараз',
    planned: 'Класи {{day}}',
    live: 'зараз',
    next: 'далі о {{time}}',
    done: 'уроки закінчились',
    formats: { in_person: 'очно', online: 'онлайн', shelter: 'в укритті' },
  },
  controlHour: {
    title: 'Контрольний час',
    hint: 'Переведіть класи на день онлайн або в укриття: розклад зміниться сам, учні, батьки й учителі отримають сповіщення. Уроки, що вже минули, не змінюються.',
    today: 'Сьогодні',
    tomorrow: 'Завтра',
    formats: { online: 'Онлайн', shelter: 'В укритті', in_person: 'Очно' },
    wholeSchool: 'Уся школа',
    classes: 'Класи',
    apply: 'Застосувати',
    confirm: 'Перевести {{who}} {{day}}: {{format}}?',
    who: { school: 'всю школу', classes: 'вибрані класи ({{count}})' },
    done_one: 'Змінено {{count}} урок. Сповіщення надіслано.',
    done_few: 'Змінено {{count}} уроки. Сповіщення надіслано.',
    done_many: 'Змінено {{count}} уроків. Сповіщення надіслано.',
    done_other: 'Змінено {{count}} уроку. Сповіщення надіслано.',
    nothing: 'Змінювати нічого: уроки вже в цьому форматі або минули.',
    norms: 'Синхронні уроки у воєнний час (МОЗ №1371):',
    normGrades: '{{from}}–{{to}} кл.',
  },
  canteen: {
    title: 'Меню їдальні',
    today: 'Сьогодні в їдальні',
    week: 'Тиждень',
    prevWeek: '← Попередній',
    nextWeek: 'Наступний →',
    thisWeek: 'Цей тиждень',
    empty: 'Меню на цей день ще не додали.',
    emptyWeek: 'Меню на цей тиждень ще не додали.',
    meals: { breakfast: 'Сніданок', lunch: 'Обід', snack: 'Полуденок' },
    allergens: {
      gluten: 'Глютен',
      crustaceans: 'Ракоподібні',
      eggs: 'Яйця',
      fish: 'Риба',
      peanuts: 'Арахіс',
      soy: 'Соя',
      milk: 'Молоко',
      nuts: 'Горіхи',
      celery: 'Селера',
      mustard: 'Гірчиця',
      sesame: 'Кунжут',
      sulphites: 'Сульфіти',
      lupin: 'Люпин',
      molluscs: 'Молюски',
    },
    avoidTitle: 'Чого уникаємо',
    avoidHint:
      'Позначте алергени — страви з ними будуть підсвічені. Вибір зберігається лише на цьому пристрої й нікуди не передається.',
    contains: 'Містить: {{list}}',
    warning: 'Увага: {{list}}',
    lawHint: '14 алергенів за Законом «Про інформацію для споживачів щодо харчових продуктів».',
    add: 'Додати страву',
    dish: 'Страва',
    note: 'Вихід / примітка',
    notePlaceholder: '200 г',
    copyWeek: 'Скопіювати попередній тиждень',
    copyDone: 'Скопійовано страв: {{count}}',
    remove: 'Прибрати',
  },
  events: {
    title: 'Події',
    upcoming: 'Найближчі події',
    empty: 'Найближчим часом подій немає.',
    new: 'Нова подія',
    kinds: {
      celebration: 'Свято',
      meeting: 'Збори',
      trip: 'Екскурсія',
      exam: 'Контрольна / олімпіада',
      holiday: 'Канікули / вихідні',
      other: 'Інше',
    },
    kind: 'Тип',
    titleLabel: 'Назва',
    description: 'Опис',
    from: 'Дата',
    to: 'До (необов’язково)',
    time: 'Час (необов’язково)',
    place: 'Місце',
    audience: 'Для кого',
    today: 'Сьогодні',
    tomorrow: 'Завтра',
    remove: 'Видалити',
    allDay: 'Увесь день',
  },
  polls: {
    title: 'Опитування',
    empty: 'Опитувань поки немає.',
    new: 'Нове опитування',
    question: 'Питання',
    options: 'Варіанти відповіді',
    option: 'Варіант {{n}}',
    addOption: '+ Ще варіант',
    multiple: 'Можна обрати кілька',
    closesAt: 'Приймати відповіді до',
    audience: 'Кого питаємо',
    onlyAdults: 'Опитування — лише для дорослих: опитування дітей потребують окремої згоди.',
    anonymous: 'Відповіді анонімні: автор бачить лише кількість.',
    send: 'Надіслати',
    change: 'Змінити відповідь',
    answered: 'Ви відповіли',
    open: 'Відповіді приймаються',
    openUntil: 'до {{date}}',
    closed: 'Завершено',
    close: 'Завершити',
    remove: 'Видалити',
    stats: 'Відповіли {{answered}} з {{audience}}',
    resultsAfter: 'Результати буде видно після вашої відповіді.',
  },
  achievements: {
    title: 'Досягнення',
    portfolio: 'Портфоліо',
    board: 'Дошка досягнень класу',
    boardHint:
      'Тут лише діти, чиї батьки погодилися показувати досягнення класу. Оцінок і рейтингів немає.',
    boardEmpty: 'На дошці поки порожньо.',
    empty: 'Досягнень поки немає.',
    recent: 'Нещодавні досягнення',
    add: 'Додати досягнення',
    student: 'Учень',
    class: 'Клас',
    titleLabel: 'Що досягнуто',
    titlePlaceholder: 'ІІ етап олімпіади з математики — диплом ІІ ступеня',
    description: 'Подробиці (необов’язково)',
    date: 'Дата',
    category: 'Напрям',
    level: 'Рівень',
    remove: 'Видалити',
    categories: {
      olympiad: 'Олімпіади',
      sport: 'Спорт',
      art: 'Творчість',
      science: 'Наука',
      volunteering: 'Волонтерство',
      other: 'Інше',
    },
    levels: {
      class: 'Клас',
      school: 'Школа',
      city: 'Місто',
      region: 'Область',
      national: 'Україна',
      international: 'Міжнародний',
    },
  },
  materials: {
    title: 'Матеріали до уроків',
    empty: 'Матеріалів поки немає.',
    course: 'Предмет',
    addLink: 'Додати посилання',
    addFile: 'Завантажити файл',
    titleLabel: 'Назва',
    url: 'Посилання (https://…)',
    file: 'Файл до 20 МБ: PDF, Word, PowerPoint, Excel, зображення, аудіо, відео',
    tooBig: 'Файл більший за 20 МБ.',
    open: 'Відкрити',
    link: 'Посилання',
    remove: 'Видалити',
    uploading: 'Завантажуємо…',
    hint: 'Матеріали бачать учні цього предмета та їхні батьки.',
    sim: 'Симуляція · PhET',
    addSim: 'Додати симуляцію з лабораторії',
    add: 'Додати матеріал',
    pickSim: 'Симуляція',
    forTopic: 'до теми',
  },
  board: {
    button: 'Дошка',
    showVideo: 'Відео',
    showBoard: 'Дошка',
    loading: 'Відкриваємо дошку…',
    notFound: 'Дошку не знайдено або вона ще не відкрита для вас.',
    leave: 'До матеріалів',
    viewOnly: 'перегляд',
    class: 'Клас',
    closeForAll: 'Закрити дошку для всіх',
    on: 'увімк.',
    off: 'вимк.',
    react: 'Реакція',
    fit: 'Показати все',
    more: 'Ще',
    export: 'Зберегти картинкою',
    style: { sketchy: 'Рукописний стиль', clean: 'Рівний стиль' },
    background: { dots: 'Фон у крапку', plain: 'Білий фон' },
    save: {
      saved: 'Збережено',
      saving: 'Зберігаємо…',
      offline: 'Немає звʼязку — збережемо пізніше',
      error: 'Не вдалося зберегти',
    },
    list: {
      title: 'Дошки',
      hint: 'Дошка, яку ви показали класу на уроці, лишається в учнів і батьків для повторення.',
      new: 'Нова дошка',
      titleLabel: 'Назва дошки',
      empty: 'Дошок поки немає.',
      open: 'Відкрити',
      remove: 'Видалити',
      removeSure: 'Точно видалити?',
      shared: 'бачить клас',
      notShared: 'бачите лише ви',
      share: 'Показати класу',
      unshare: 'Сховати від класу',
    },
    pick: {
      title: 'Дошка уроку',
      new: 'Нова дошка',
      none: 'Дошок цього предмета ще немає.',
      thisLesson: 'до цього уроку',
    },
    tools: {
      select: 'Вибір',
      hand: 'Рука — рухати дошку',
      pen: 'Ручка (ще раз — колір)',
      marker: 'Маркер',
      eraser: 'Ластик',
      sticky: 'Стікер',
      shapes: 'Фігури',
      rectangle: 'Прямокутник',
      ellipse: 'Еліпс',
      diamond: 'Ромб',
      line: 'Лінія',
      frame: 'Рамка-розділ',
      arrow: 'Стрілка',
      text: 'Текст',
      image: 'Картинка',
      stamp: 'Печатка',
      templates: 'Шаблони',
      styles: 'Колір, шрифт, шари вибраного',
      laser: 'Лазерна указка',
      comment: 'Коментар',
      formula: 'Формула',
    },
    colors: {
      yellow: 'Жовтий',
      green: 'Зелений',
      blue: 'Блакитний',
      pink: 'Рожевий',
      orange: 'Помаранчевий',
      violet: 'Фіолетовий',
    },
    templates: {
      coordinates: 'Координатна площина',
      numberLine: 'Числова пряма',
      kanban: 'Треба · Роблю · Готово',
      compare: 'За і проти',
      venn: 'Круги Ейлера',
      mindMap: 'Ментальна карта',
      timeline: 'Хронологія',
      table: 'Таблиця',
      grid: 'Клітинка',
      gridOff: 'Без клітинки',
    },
    pdf: {
      add: 'PDF або презентація (PDF)',
      busy: 'Додаємо сторінки…',
      page: 'Сторінка {{n}}',
      failed: 'Не вдалося відкрити PDF',
    },
    who: {
      title: 'Хто малює',
      all: 'Усі',
      teacher: 'Лише я',
      some: 'Вибрані',
      someHint: 'Позначте учнів олівцем у списку людей.',
    },
    follow: {
      lead: 'Усі за мною',
      bring: 'Привести всіх до мене',
      led: 'Ви бачите те, що показує вчитель',
      following: 'Ви стежите за: {{name}}',
      stop: 'Перестати',
    },
    hidden: {
      title: 'Відповіді приховано',
      teacher: 'Учні не бачать відповідей одне одного',
      student: 'Вашу відповідь бачить лише вчитель',
      reveal_one: 'Показати відповіді ({{count}})',
      reveal_few: 'Показати відповіді ({{count}})',
      reveal_many: 'Показати відповіді ({{count}})',
      reveal_other: 'Показати відповіді ({{count}})',
    },
    timer: {
      title: 'Таймер',
      minutes_one: '{{count}} хв',
      minutes_few: '{{count}} хв',
      minutes_many: '{{count}} хв',
      minutes_other: '{{count}} хв',
      over: 'Час вийшов!',
      stop: 'Зупинити',
    },
    vote: {
      title: 'Голосування',
      start: 'Почати (3 голоси кожному)',
      show: 'Показати результати',
      end: 'Завершити',
      given_one: 'Віддано {{count}} голос',
      given_few: 'Віддано {{count}} голоси',
      given_many: 'Віддано {{count}} голосів',
      given_other: 'Віддано {{count}} голосу',
      hint_one: 'Голосування: лишився {{count}} голос — торкніться стікера чи фігури',
      hint_few: 'Голосування: лишилося {{count}} голоси — торкніться стікера чи фігури',
      hint_many: 'Голосування: лишилося {{count}} голосів — торкніться стікера чи фігури',
      hint_other: 'Голосування: лишилося {{count}} голосу',
    },
    comments: {
      title: 'Коментар',
      resolved: 'Вирішено',
      placeholder: 'Напишіть коментар…',
      reply: 'Відповісти…',
      send: 'Надіслати',
      resolve: 'Вирішено',
      reopen: 'Повернути',
      remove: 'Видалити',
      open: 'Коментар від {{name}}',
      hide: 'Сховати коментарі',
      show: 'Показати коментарі',
    },
    formula: {
      title: 'Формула',
      hint: 'Наберіть формулу або зберіть її кнопками вище',
      failed: 'Не вдалося прочитати формулу',
      insert: 'Вставити',
      save: 'Зберегти',
      cancel: 'Скасувати',
      edit: 'Змінити формулу',
    },
    present: {
      start: 'Презентація по рамках',
      none: 'Додайте рамки (Фігури → Рамка) — це будуть слайди',
      slide: 'Слайд {{n}} з {{total}}',
      prev: 'Попередній слайд',
      next: 'Наступний слайд',
      end: 'Завершити',
      watching: 'Учитель показує слайд {{n}} з {{total}}',
    },
    minimap: { show: 'Мінікарта', hide: 'Сховати мінікарту', label: 'Уся дошка' },
    picture: {
      none: 'Учитель ще не зберіг картинку цієї дошки — загляньте пізніше.',
      hint: 'Збільшуйте двома пальцями',
      updated: 'оновлено {{date}}',
    },
    people: {
      title_one: 'На дошці {{count}} людина',
      title_few: 'На дошці {{count}} людини',
      title_many: 'На дошці {{count}} людей',
      title_other: 'На дошці {{count}} людини',
      teacher: 'вчитель',
      active: 'малює зараз',
      idle: 'неактивний',
      find: 'Знайти',
      follow: 'Стежити',
      unfollow: 'Не стежити',
      hideCursors: 'Сховати курсори інших',
    },
  },
  album: {
    title: 'Фотоальбом класу',
    empty: 'Альбомів поки немає.',
    noPhotos: 'У цьому альбомі ще немає фото.',
    photos_one: '{{count}} фото',
    photos_few: '{{count}} фото',
    photos_many: '{{count}} фото',
    photos_other: '{{count}} фото',
    new: 'Новий альбом',
    titleLabel: 'Назва альбому',
    takenOn: 'Дата',
    create: 'Створити',
    upload: 'Додати фото',
    uploading: 'Завантажуємо {{done}} з {{total}}…',
    back: '← Усі альбоми',
    remove: 'Видалити',
    removeAlbum: 'Видалити альбом',
    onlyClass: 'Альбом бачить лише клас: учні, їхні батьки та вчителі класу.',
    privacy:
      'Перед збереженням сервер видаляє з фото всі метадані: геомітки, модель телефона, дату зйомки.',
    rules: 'Не публікуйте фото укриттів та інфраструктури школи.',
    noConsent: 'Без згоди батьків — не має бути на фото:',
    allConsent: 'Батьки всіх дітей класу погодилися на фото.',
    errors: {
      not_allowed: 'Додавати фото можуть лише вчителі цього класу.',
      too_large: 'Фото завелике.',
      unsupported: 'Цей формат не підтримується: оберіть JPEG або PNG.',
      server_error: 'Не вдалося завантажити фото. Спробуйте ще раз.',
    },
  },
  quizzes: {
    title: 'Тести',
    empty: 'Тестів поки немає.',
    new: 'Новий тест',
    take: 'Пройти тест',
    send: 'Надіслати відповіді',
    sending: 'Надсилаємо…',
    due: 'до {{date}}',
    closed: 'Завершено',
    notTaken: 'Ще не пройдено',
    notTakenParent: 'Дитина ще не пройшла тест',
    missed: 'Не пройдено',
    score: '{{score}} з {{max}}',
    yourScore: 'Ваш результат: {{score}} з {{max}}',
    review: 'Переглянути відповіді',
    hideReview: 'Сховати відповіді',
    noReview: 'Учитель не відкрив правильні відповіді.',
    questions_one: '{{count}} запитання',
    questions_few: '{{count}} запитання',
    questions_many: '{{count}} запитань',
    questions_other: '{{count}} запитання',
    once: 'Відповісти можна один раз.',
    fairHint: 'Учитель побачить, якщо ти виходитимеш із тесту, — і просто уточнить у тебе.',
    away_one: 'виходили з тесту {{count}} раз · {{time}}',
    away_few: 'виходили з тесту {{count}} рази · {{time}}',
    away_many: 'виходили з тесту {{count}} разів · {{time}}',
    away_other: 'виходили з тесту {{count}} рази · {{time}}',
    attemptNo: 'спроба {{n}}',
    firstScore: 'перша: {{score}} з {{max}}',
    allowRetake: 'Дозволити ще спробу',
    retakeOpen: 'Ще спроба відкрита',
    retakeTake: 'Пройти ще раз',
    retakeParent: 'Учитель дозволив ще спробу',
    multipleHint: 'Можна обрати кілька',
    unanswered: 'Без відповіді: {{count}}',
    course: 'Предмет',
    titleLabel: 'Назва',
    description: 'Пояснення (необов’язково)',
    dueOn: 'Дедлайн',
    showAnswers: 'Після відповіді показати учням правильні відповіді',
    question: 'Запитання {{n}}',
    questionText: 'Текст запитання',
    option: 'Варіант {{n}}',
    addOption: '+ Варіант',
    addQuestion: '+ Запитання',
    correctHint: 'Позначте правильні варіанти.',
    publish: 'Опублікувати',
    results: 'Результати',
    toTake: 'Треба пройти',
    waiting_one: '{{count}} тест чекає',
    waiting_few: '{{count}} тести чекають',
    waiting_many: '{{count}} тестів чекають',
    waiting_other: '{{count}} тесту чекають',
    students: 'Учні',
    byQuestion: 'За запитаннями',
    averageLabel: 'Середній результат',
    answered: 'Пройшли {{answered}} з {{students}}',
    average: 'Середній результат: {{value}}%',
    rightShare: 'правильно {{value}}%',
    remove: 'Видалити тест',
    back: '← Усі тести',
    errors: {
      closed: 'Тест уже завершено.',
      already: 'Ви вже відповіли на цей тест.',
    },
  },
  birthdays: {
    title: 'Дні народження',
    today: 'сьогодні',
    tomorrow: 'завтра',
    empty: 'Найближчим часом днів народження немає.',
  },
  consents: {
    title: 'Згоди батьків',
    hint: 'Кожен із батьків дає згоду від свого імені й може відкликати її будь-коли.',
    kinds: {
      birthday: 'Показувати день народження класу',
      achievements: 'Показувати досягнення на дошці класу',
      photo: 'Дитина може бути на фото в альбомі класу',
    },
    hints: {
      birthday: 'Однокласники та їхні батьки бачать лише день і місяць, без року.',
      achievements: 'Без оцінок і рейтингів — тільки досягнення.',
      photo: 'Потрібна згода всіх батьків дитини. Альбом бачить лише клас.',
    },
    agreed: 'Погодилися {{agreed}} з {{guardians}}',
  },
  push: {
    inviteTitle: 'Увімкніть сповіщення',
    inviteBody:
      'Оцінки, домашні завдання, повідомлення від учителів і тривоги прийдуть одразу на телефон. Вночі — тиша: тихі години можна змінити в профілі.',
    enable: 'Увімкнути сповіщення',
    enabled: 'Сповіщення на цьому телефоні увімкнено.',
    denied: 'Сповіщення для Parta вимкнено в налаштуваннях телефону.',
    openSettings: 'Відкрити налаштування',
    unavailable: 'Сповіщення працюють у застосунку з TestFlight або App Store.',
    settingsTitle: 'Сповіщення',
    quietTitle: 'Тихі години',
    quietOff: 'Без тихих годин',
    quietHint:
      'У тихі години сповіщення чекають до ранку. Тривоги й початок онлайн-уроку — завжди одразу.',
    topicsTitle: 'Що надсилати на телефон',
    topics: {
      grades: 'Оцінки',
      homework: 'Домашні завдання',
      announcements: 'Оголошення',
      messages: 'Повідомлення',
      lessons: 'Онлайн-уроки й розклад',
      absence: 'Відсутність',
      meetings: 'Зустрічі з учителем',
      school: 'Події, опитування, досягнення',
      digest: 'Тижневий підсумок про дитину',
      alarm: 'Тривоги',
    },
    alarmHint: 'Тривоги приходять завжди — навіть у режимі «Фокус».',
    previews: 'Текст повідомлень на заблокованому екрані',
    previewsHint:
      'Вимкнено — видно лише, від кого повідомлення. Оцінки на екрані не показуються ніколи.',
    channelRegular: 'Шкільні сповіщення',
    channelAlarm: 'Тривоги',
  },
  alarm: {
    title: 'Тривога в школі',
    kinds: {
      air: 'Повітряна тривога',
      drill: 'Навчальна тривога',
      mining: 'Мінування',
      evacuation: 'Евакуація',
    },
    simulated: 'симуляція',
    duration: 'Триває {{minutes}} хв',
    longDay: 'Понад 2 години тривог сьогодні',
    noActive: 'Зараз тривоги немає.',
    start: {
      title: 'Запустити',
      drill: 'Навчальна тривога',
      air: 'Симулювати повітряну тривогу',
      mining: 'Мінування',
      evacuation: 'Евакуація',
      hint: 'Повітряна тривога в продакшні вмикається сама за офіційними даними для громади школи. На демо її можна симулювати.',
    },
    end: 'Відбій',
    endOfficial: 'Відбій настане за офіційними даними',
    banner: {
      staff: '{{kind}}! Ведіть клас до укриття й проведіть перекличку',
      student: '{{kind}}. Йди з учителем до укриття',
      guardian: 'У школі {{kind}}. Діти з учителями йдуть до укриття',
      noPickup: 'Не забирайте дитину під час тривоги.',
      open: 'Відкрити',
    },
    child: {
      pending: '{{name}}: ще не відмічено',
      marked: '{{name}}: {{status}}, {{time}}',
    },
    status: {
      in_shelter: 'в укритті',
      absent: 'відсутній у школі',
      not_found: 'не знайдено',
      needs_help: 'потрібна допомога',
      safe_elsewhere: 'в безпеці (не в школі)',
    },
    imSafe: 'Я не в школі й у безпеці',
    stats: {
      classes: 'Класи в укритті',
      students: 'Учні в укритті',
      notFound: 'Не знайдено',
      needsHelp: 'Потрібна допомога',
      unmarked: 'Не відмічено',
    },
    shelters: 'Укриття',
    occupancy: '{{occupied}} з {{capacity}}',
    classesTitle: 'Перекличка по класах',
    confirmed: 'в укритті з {{time}}',
    notConfirmed: 'ще не підтверджено',
    rollCall: 'Перекличка',
    myClass: 'Клас',
    weAreInShelter: 'Ми в укритті',
    allPresent: 'Усі решта — в укритті',
    complete: 'Перекличку завершено',
    templates: 'Повідомлення за шаблоном МОН',
    templateTexts: {
      start:
        'Увага! У школі оголошено повітряну тривогу. Освітній процес тимчасово призупинено, учні разом із вчителями перебувають в укритті. Просимо батьків не забирати дітей до відбою.',
      end: 'Відбій тривоги. Освітній процес продовжується за розкладом.',
      online:
        'Через тривалу тривогу наступні уроки відбудуться дистанційно за розкладом. Посилання на уроки — у застосунку.',
    },
    send: 'Надіслати всій школі',
    sent: 'Надіслано',
    history: 'Історія та втрачений час',
    historyColumns: {
      date: 'Дата',
      kind: 'Тип',
      duration: 'Тривалість',
      lessons: 'Уроків в укритті',
    },
    minutes: '{{count}} хв',
    go: {
      staff: 'Ведіть клас до укриття й проведіть перекличку',
    },
    calmHint: {
      student:
        'Коли почнеться тривога, тут будуть дихання й завдання в укритті — вони працюють без інтернету.',
      guardian: 'Під час тривоги тут буде видно, чи дитина вже в укритті.',
      teacher: 'Під час тривоги тут буде перекличка вашого класу.',
      admin:
        'Під час тривоги тут будуть перекличка всіх класів, укриття й повідомлення за шаблоном МОН.',
    },
  },
  lesson: {
    boss: {
      button: 'Бос теми',
      title: 'Битва з босом теми',
      pick: 'Оберіть тест курсу — кожна правильна відповідь класу стане ударом по босу.',
      noQuizzes: 'У курсу ще немає тестів. Створіть тест у розділі «Тести».',
      questions_one: '{{count}} питання',
      questions_few: '{{count}} питання',
      questions_many: '{{count}} питань',
      questions_other: '{{count}} питання',
      name: 'Бос «{{title}}»',
      question: 'Питання {{n}} з {{total}}',
      answered: 'відповіли {{count}} з {{total}}',
      multiple: 'Тут кілька правильних відповідей — обери всі.',
      send: 'Відповісти',
      accepted: 'Відповідь прийнято! Чекаємо інших…',
      hit: 'Влучно!',
      miss: 'Мимо — наступного разу влучиш.',
      late: 'Час вийшов — наступне питання твоє!',
      hits: 'влучили {{hits}} з {{total}}',
      reveal: 'Показати відповідь',
      next: 'Наступне питання',
      result: 'Результат бою',
      finish: 'Завершити бій',
      close: 'Закрити',
      won: 'Бос переможений!',
      wonHint: 'Ви зробили це разом.',
      lost: 'Бос утік…',
      lostHint: 'Повторимо тему — і буде реванш.',
      allHits_one: 'Усього {{count}} влучання.',
      allHits_few: 'Усього {{count}} влучання.',
      allHits_many: 'Усього {{count}} влучань.',
      allHits_other: 'Усього {{count}} влучання.',
    },
    signal: {
      title: 'Світлофор',
      green: 'Зрозуміло',
      yellow: 'Не зовсім',
      red: 'Не зрозуміло',
      hint: 'Тихо покажи вчителю, чи зрозуміло. Однокласники твого сигналу не бачать.',
      teacherTitle: 'Світлофор розуміння',
      none: 'Поки ніхто не показав сигнал.',
      summary: 'Показали {{answered}} з {{students}} · не зрозуміли або не зовсім — {{percent}}%',
      private: 'Учні бачать лише свій сигнал — без тиску й без порівнянь.',
      reset: 'Нове питання — скинути',
    },
    join: 'Приєднатися',
    start: 'Почати урок',
    connecting: 'Підключаємося до уроку…',
    leave: 'Вийти з уроку',
    roles: {
      teacher: 'Ви ведете урок',
      student: 'Ви учасник',
      observer: 'Ви спостерігач',
    },
    errors: {
      not_allowed: 'Цей урок недоступний для вашого облікового запису.',
      not_online: 'Цей урок проходить не онлайн.',
      not_today: 'Підключитися можна в день уроку.',
      locked: 'Учитель закрив урок: увійти зараз не можна.',
      invalid_session: 'Сесія завершилася. Увійдіть знову.',
      server_error: 'Не вдалося підключитися до уроку. Спробуйте ще раз.',
      online_lesson: 'Це онлайн-урок: дошка відкривається всередині уроку.',
    },
    back: 'До розкладу',
    eyes: {
      badge: 'пауза для очей через {{count}} хв',
      badgeDue: 'час на паузу для очей',
      normHint: 'Норма безперервної роботи з екраном для {{grade}} класу — {{minutes}} хв (МОЗ).',
      dueStudent: 'Час на хвилинну паузу для очей',
      dueTeacher: 'Учням час на паузу для очей',
      start: 'Почати паузу',
      startForAll: 'Пауза для всіх',
      later: 'Через 5 хв',
      title: 'Пауза для очей',
      step1: 'Подивіться у вікно або вдалину — 20 секунд.',
      step2: 'Міцно заплющіть очі на 3 секунди й розплющіть. Повторіть 5 разів.',
      step3: 'Часто покліпайте 15 секунд, потім посидьте із заплющеними очима.',
      done: 'Повернутися до уроку',
      classOnBreak: 'Клас робить паузу для очей',
      end: 'Завершити',
    },
    demoNow: 'Демо: онлайн-урок зараз',
    inAppOnly: 'Цей урок відкривається в застосунку «Parta».',
    live: {
      now: 'Зараз онлайн-урок',
      until: 'до {{time}}',
      inRoom: '{{name}} на уроці з {{time}}',
      left: '{{name}} не на уроці з {{time}}',
      notYet: '{{name}} ще не на уроці',
    },
    presence: {
      title: 'Присутність на онлайн-уроці',
      joined: '{{from}}–{{to}} · {{minutes}} хв',
      inRoom: 'на уроці з {{time}}',
      never: 'не підключалися',
      markAbsent: 'Позначити відсутніх за даними уроку',
      marked: 'Позначено відсутніх: {{count}}',
      hint: 'Хто не приєднався до відеоуроку, отримає «н». Позначки тих, хто був, буде знято.',
    },
    hand: {
      raise: 'Підняти руку',
      lower: 'Опустити руку',
      raised: 'Підняті руки',
      none: 'Піднятих рук немає',
      dismiss: 'Опустити',
    },
    moderation: {
      students: 'Учні на уроці',
      nobody: 'Учнів поки немає.',
      giveFloor: 'Дати слово',
      takeFloor: 'Забрати слово',
      mute: 'Вимкнути мікрофон',
      remove: 'Видалити з уроку',
      removeSure: 'Точно видалити?',
      lock: 'Замок',
      locked: 'Урок закрито',
      lockHint: 'Закритий урок: нові учні не зможуть увійти.',
      policy: { open: 'Мікрофони вільно', permission: 'Мікрофони за дозволом' },
      policyHint: 'За дозволом: учні говорять, коли ви дасте слово.',
      waitFloor: 'Мікрофон і камера — за дозволом учителя. Підніміть руку',
      floorGiven: 'Учитель дав вам слово — увімкніть мікрофон',
      mics: 'Мікрофони учнів',
      micsOpen: 'вільно',
      micsPermission: 'за дозволом',
      door: 'Вхід на урок',
      doorOpen: 'відкрито',
      doorLocked: 'закрито',
    },
    devices: {
      micOn: 'Вимкнути мікрофон',
      micOff: 'Увімкнути мікрофон',
      camOn: 'Вимкнути камеру',
      camOff: 'Увімкнути камеру',
      choose: 'Обрати пристрій',
      microphones: 'Мікрофон',
      cameras: 'Камера',
      default: 'Типовий пристрій',
      share: 'Показати екран',
      stopShare: 'Зупинити показ',
      startAudio: 'Увімкнути звук уроку',
    },
    tile: {
      weak: 'Слабкий звʼязок',
      hand: 'Піднята рука',
    },
    screen: {
      ask: 'Показати екран',
      waiting: 'Чекаємо дозволу…',
      start: 'Почати показ',
      stop: 'Зупинити показ',
      given: 'Учитель дозволив показати екран — натисніть «Почати показ»',
      declined: 'Учитель поки не дозволив показ екрана',
      asks: 'Хочуть показати екран',
      none: 'Запитів немає',
      allow: 'Дозволити',
      decline: 'Відхилити',
      showing: 'показує екран',
      give: 'Дозволити екран',
      take: 'Забрати екран',
      oneAtATime: 'Показувати екран може один учень — попередній показ зупиниться.',
      tile: 'екран',
    },
    bubbles: {
      bigger: 'Більші віконця',
      smaller: 'Кружечки',
      hide: 'Сховати відео',
      fewer: 'Менше',
    },
    muteAll: 'Вимкнути мікрофони учням',
    mutedByTeacher: 'Мікрофони учнів вимкнено',
    chat: { title: 'Чат уроку', short: 'Чат', empty: 'Повідомлень поки немає.' },
    alarm: {
      paused: 'Урок призупинено. Пройдіть до укриття — урок продовжиться після відбою.',
      safeMarked: 'Ви позначили, що в безпеці.',
      resume: 'Продовжити урок',
      resumeHint: 'Якщо всі учні в укриттях і мають звʼязок',
      resumed: 'Урок продовжено під час тривоги',
      ended: 'Відбій тривоги — урок продовжується',
    },
  },
  offline: {
    banner: 'Немає звʼязку',
    since: 'дані станом на {{time}}',
    queued_one: 'очікує відправлення: {{count}}',
    queued_few: 'очікують відправлення: {{count}}',
    queued_many: 'очікують відправлення: {{count}}',
    queued_other: 'очікують відправлення: {{count}}',
    back: 'Звʼязок відновлено',
    waiting: 'Очікує звʼязку',
    queuedAnswer: 'Відповідь збережено на пристрої — надішлемо, щойно зʼявиться звʼязок.',
    queuedRequest:
      'Заяву збережено на телефоні — надішлемо класному керівнику, щойно зʼявиться звʼязок.',
    queuedMessage: 'надішлеться зі звʼязком',
  },
  requests: {
    title: 'Заяви про відсутність',
    new: 'Заява про відсутність: {{name}}',
    newShort: 'Нова заява',
    when: 'Коли',
    today: 'Сьогодні',
    tomorrow: 'Завтра',
    days: 'Скільки днів',
    sent: 'Заяву надіслано класному керівнику',
    decided: 'Розглянуті',
    open: 'Подати заяву про відсутність',
    homeCard_one: 'Заяви на розгляді: {{count}}',
    homeCard_few: 'Заяви на розгляді: {{count}}',
    homeCard_many: 'Заяви на розгляді: {{count}}',
    homeCard_other: 'Заяви на розгляді: {{count}}',
    from: 'З',
    to: 'По',
    reason: 'Причина',
    reasons: {
      illness: 'Хвороба',
      medical: 'Візит до лікаря',
      family: 'Сімейні обставини',
      alarm_night: 'Нічна тривога',
      other: 'Інше',
    },
    comment: 'Коментар',
    commentHint: 'Наприклад: прийде з 3-го уроку',
    privacy:
      'Причину бачать лише класний керівник і адміністрація школи. Вчителі-предметники бачать тільки, що відсутність поважна.',
    send: 'Надіслати заяву',
    empty: 'Заяв поки немає.',
    nothingPending: 'Нових заяв немає.',
    pending_one: 'На розгляді · {{count}}',
    pending_few: 'На розгляді · {{count}}',
    pending_many: 'На розгляді · {{count}}',
    pending_other: 'На розгляді · {{count}}',
    all: 'Усі',
    author: 'Подав(-ла)',
    reply: 'Відповідь (необовʼязково)',
    replyShort: 'Відповідь',
    accept: 'Прийняти',
    reject: 'Відхилити',
    decide: 'Розглянути',
    withdraw: 'Відкликати',
    covered: 'Поважна причина (заява)',
    status: {
      submitted: 'На розгляді',
      accepted: 'Прийнято',
      rejected: 'Відхилено',
    },
  },
  meetings: {
    title: 'Зустрічі з учителями',
    titleStaff: 'Зустрічі з батьками',
    forParents: 'Записатися на зустріч можуть батьки.',
    addHours: 'Години прийому',
    date: 'День',
    from: 'З',
    to: 'До',
    length: 'Тривалість однієї зустрічі',
    minutes_one: '{{count}} хвилина',
    minutes_few: '{{count}} хвилини',
    minutes_many: '{{count}} хвилин',
    minutes_other: '{{count}} хвилини',
    online: 'Онлайн',
    inPerson: 'Очно',
    place: 'Де',
    placeHint: 'Наприклад: каб. 203',
    publish: 'Опублікувати',
    wrongHours: 'Перевірте день і час: години прийому мають бути в майбутньому, до 6 годин.',
    noHours: 'Годин прийому ще немає. Додайте їх — батьки зможуть записатися.',
    booked: 'записано {{booked}} з {{total}}',
    free: 'Вільно',
    about: 'дитина: {{name}}',
    cancel: 'Скасувати',
    remove: 'Прибрати',
    join: 'Приєднатися',
    joinSoon: 'Онлайн-зустріч: кнопка «Приєднатися» зʼявиться за 10 хвилин до початку.',
    open: 'Записатися на зустріч з учителем',
    leave: 'Завершити зустріч',
    back: 'До зустрічей',
    demoNow: 'Демо: зустріч зараз',
    mine: 'Мої зустрічі',
    noneMine: 'Запланованих зустрічей немає.',
    bookTitle: 'Записатися на зустріч · {{name}}',
    bookWith: 'Запис: {{name}}',
    noFree: 'Вільного часу зараз немає.',
    noHoursShort: 'немає вільного часу',
    freeSlots_one: '{{count}} вільне місце',
    freeSlots_few: '{{count}} вільні місця',
    freeSlots_many: '{{count}} вільних місць',
    freeSlots_other: '{{count}} вільного місця',
    topic: 'Про що хочете поговорити',
    topicHint: 'Наприклад: успішність з алгебри',
    book: 'Записатися',
    bookFailed:
      'Не вдалося записатися: можливо, цей час щойно зайняли або ви вже записані до цього вчителя.',
    errors: {
      not_allowed: 'Ця зустріч недоступна для вашого облікового запису.',
      not_online: 'Ця зустріч проходить очно.',
      not_today: 'Підключитися можна за 10 хвилин до початку.',
      locked: 'Зустріч зараз недоступна.',
      invalid_session: 'Сесія завершилася. Увійдіть знову.',
      server_error: 'Не вдалося підключитися. Спробуйте ще раз.',
      online_lesson: 'Зустріч зараз недоступна.',
    },
  },
  care: {
    title: 'Турбота',
    hand: 'як ти?',
    promise:
      'Батьки, вчителі й директор не бачать твоїх звернень і настрою. Психолог бачить лише те, що ти напишеш.',
    promiseShort: 'Бачите тільки ти і психолог',
    noAi: 'Тут немає штучного інтелекту: тобі відповідає жива людина.',
    bands: {
      '1-4': '1–4 клас',
      '5-9': '5–9 клас',
      '10-11': '10–11 клас',
    },
    topics: {
      friends: 'Друзі й однокласники',
      study: 'Навчання',
      family: 'Вдома',
      feelings: 'Почуття',
      other: 'Інше',
    },
    moods: {
      great: 'Чудово',
      good: 'Добре',
      okay: 'Так собі',
      sad: 'Сумно',
      angry: 'Сердито',
      anxious: 'Тривожно',
    },
    mood: {
      question: 'Як ти сьогодні?',
      saved: 'Записали. Це бачиш тільки ти.',
      change: 'Змінити',
      hard: 'Буває. Хочеш подихати разом або написати психологу?',
      good: 'Класно! Гарного дня.',
      history: 'Мій настрій за 2 тижні',
      historyEmpty: 'Відмічай настрій щодня — тут буде видно, як змінюються твої дні.',
      queued: 'Збережено на телефоні — запишемо, щойно зʼявиться звʼязок.',
    },
    actions: {
      write: 'Написати психологу',
      writeHint: 'Анонімно, як скринька довіри',
      sos: 'Мені погано зараз',
      sosHint: 'Телефони допомоги цілодобово',
      calm: 'Заспокоїтись',
      calmHint: 'Дихання і вправа «5-4-3-2-1»',
      bullying: 'Повідомити про булінг',
      bullyingHint: 'Можна анонімно',
      meet: 'Зустріч із психологом',
      meetHint: 'Поговорити наживо',
    },
    threads: {
      title: 'Мої звернення',
      empty: 'Тут зʼявляться твої розмови з психологом.',
      unread: 'Нова відповідь',
      waiting: 'Чекає відповіді',
      answered: 'Є відповідь',
      closed: 'Закрито',
      bullying: 'Булінг',
      complaint: 'Скарга на повідомлення',
      urgent: 'Терміново',
    },
    write: {
      title: 'Написати психологу',
      topic: 'Про що хочеш поговорити?',
      placeholder: 'Напиши, що тебе турбує. Можна коротко — психолог допоможе розібратися.',
      who: 'Що побачить психолог',
      anonymous: 'Анонімно',
      anonymousHint: 'Психолог побачить лише «{{band}}».',
      showClass: 'Показати клас',
      showClassHint: 'Психолог побачить «{{class}}».',
      showName: 'Назватися',
      showNameHint: 'Психолог побачить твоє імʼя і клас.',
      later: 'Назватися можна й пізніше — прямо в розмові.',
      send: 'Надіслати',
      sent: 'Надіслано. Психолог відповість тут — зазвичай у робочий час.',
      queued: 'Збережено на телефоні — надішлемо, щойно зʼявиться звʼязок.',
    },
    bully: {
      title: 'Повідомити про булінг',
      intro:
        'Булінг — це коли когось цькують знову й знову: ображають, принижують, погрожують, бʼють, висміюють чи ігнорують. Ти правильно робиш, що розповідаєш.',
      who: 'Кого цькують?',
      me: 'Мене',
      other: 'Іншу людину',
      where: 'Де це відбувається?',
      places: {
        class: 'У класі',
        corridor: 'У коридорі',
        canteen: 'В їдальні',
        yard: 'На подвірʼї',
        online: 'В інтернеті',
        road: 'Дорогою до школи',
        elsewhere: 'Деінде',
      },
      what: 'Що сталося?',
      placeholder: 'Опиши, що відбувається. Імена — якщо не страшно.',
      routing:
        'Повідомлення отримають психолог і директор школи: за законом про булінг школа має відреагувати.',
    },
    report: {
      action: 'Поскаржитися',
      title: 'Скарга на повідомлення',
      hint: 'Психолог і директор побачать це повідомлення. Автор не дізнається, хто поскаржився.',
      placeholder: 'Чому це образливо? (необовʼязково)',
      defaultBody: 'Скарга на повідомлення в чаті.',
      send: 'Поскаржитися',
      sent: 'Скаргу надіслано. Дякуємо, що не мовчиш.',
    },
    thread: {
      you: 'Ти',
      roles: {
        psychologist: 'психолог',
        director: 'директор',
      },
      sees: 'Психолог бачить: {{who}}',
      seesDirector: 'Психолог і директор бачать: {{who}}',
      revealName: 'Назватися',
      revealClass: 'Показати клас',
      revealConfirm: 'Психолог побачить твоє імʼя і клас. Скасувати це не можна. Назватися?',
      placeholder: 'Повідомлення…',
      send: 'Надіслати',
      closed: 'Розмову закрито. Напиши — і вона відкриється знову.',
      queued: 'надішлеться зі звʼязком',
    },
    sos: {
      title: 'Мені погано зараз',
      intro: 'Ти не один і не одна. Подзвони — там вислухають цілодобово, безкоштовно й анонімно.',
      lines: {
        lifeline: {
          name: 'Lifeline Ukraine',
          hint: 'Лінія психологічної підтримки та запобігання самогубствам',
        },
        children: {
          name: 'Дитяча лінія',
          hint: 'Для дітей і молоді, з мобільного',
        },
        childrenLandline: {
          name: 'Дитяча лінія',
          hint: 'Зі стаціонарного телефону',
        },
        emergency: {
          name: 'Екстрені служби',
          hint: 'Якщо є загроза чиємусь життю',
        },
      },
      call: 'Подзвонити',
      urgent: 'Написати психологу терміново',
      urgentHint: 'Психолог побачить це першим. Уночі й у вихідні дзвони на лінію вище.',
      urgentBody: 'Мені зараз дуже погано. Потрібна допомога.',
      breathe: 'Подихати разом',
    },
    calm: {
      title: 'Заспокоїтись',
      shelter: 'Поки чекаємо на відбій — подихай разом зі мною. Це заспокоює.',
      short: 'Подихати',
      offline: 'Працює без інтернету',
      breathing: 'Дихаємо разом',
      breathingHint: 'Квадратне дихання: чотири рахунки на кожен крок.',
      phases: {
        inhale: 'Вдих',
        hold: 'Затримай',
        exhale: 'Видих',
        rest: 'Пауза',
      },
      start: 'Почати',
      stop: 'Зупинити',
      rounds_one: '{{count}} коло',
      rounds_few: '{{count}} кола',
      rounds_many: '{{count}} кіл',
      rounds_other: '{{count}} кола',
      grounding: 'Вправа «5-4-3-2-1»',
      groundingHint: 'Повертає в «тут і зараз», коли накриває тривога. Назви про себе:',
      steps: {
        see: '5 речей, які ти бачиш',
        touch: '4 речі, яких можеш торкнутися',
        hear: '3 звуки, які чуєш',
        smell: '2 запахи',
        taste: '1 смак',
      },
      next: 'Далі',
      done: 'Молодець. Як ти зараз?',
      again: 'Ще раз',
    },
    meet: {
      title: 'Зустріч із психологом',
      notAnonymous:
        'Зустріч не анонімна: психолог знатиме, хто прийде. Батьки й учителі про неї не дізнаються.',
      free: 'Вільний час',
      none: 'Зараз вільного часу немає. Напиши психологу — і ви домовитеся.',
      topic: 'Про що хочеш поговорити? (необовʼязково)',
      book: 'Записатися',
      booked: 'Записано! Зустріч зʼявилася нижче.',
      taken: 'Цей час щойно зайняли. Обери інший.',
      already: 'Ти вже записаний до цього психолога. Спершу скасуй ту зустріч.',
      mine: 'Мої зустрічі',
      online: 'Онлайн',
      inPerson: 'У кабінеті',
      join: 'Увійти',
      cancel: 'Скасувати',
    },
    parent: {
      title: 'Як підтримати дитину',
      privacy:
        'Звернення дитини до психолога конфіденційні: ні батьки, ні вчителі їх не бачать. Так діти не бояться просити про допомогу — і приходять до дорослих раніше.',
      meet: 'Записатися до психолога',
      tips: {
        listen: {
          title: 'Слухайте, а не виправляйте',
          body: 'Коли дитина ділиться, не поспішайте з порадами. Скажіть: «Я тебе чую. Розкажи ще».',
        },
        name: {
          title: 'Називайте почуття',
          body: '«Схоже, ти засмутився через контрольну» — так дитина вчиться розуміти себе.',
        },
        rhythm: {
          title: 'Тримайте ритм',
          body: 'Під час тривог і відключень світла звичний вечірній ритуал дає дитині відчуття безпеки.',
        },
        news: {
          title: 'Менше новин при дитині',
          body: 'Складне обговорюйте наодинці. Дитині — коротко, чесно й без страшних подробиць.',
        },
        help: {
          title: 'Психолог — це турбота',
          body: 'Розмова з психологом — не «проблема», а звичайна допомога. Можна піти разом.',
        },
      },
      signsTitle: 'Коли не варто чекати',
      signs:
        'Різкі зміни сну чи апетиту, втрата інтересу до улюбленого, замкнутість понад два тижні — привід поговорити з психологом. Якщо дитина говорить про смерть чи самоушкодження — дзвоніть 7333 або 112 одразу.',
      lines: 'Телефони допомоги',
    },
    staff: {
      inbox: 'Звернення',
      filters: {
        all: 'Усі',
        unread: 'Нові',
        urgent: 'Термінові',
        bullying: 'Булінг',
        closed: 'Закриті',
      },
      empty: 'Звернень поки немає.',
      anonymous: 'Анонімно',
      named: 'Назвався',
      reply: 'Відповідь…',
      send: 'Відповісти',
      close: 'Закрити розмову',
      reopen: 'Відкрити знову',
      reported: 'Повідомлення, на яке скаржаться',
      aboutSelf: 'Цькують автора звернення',
      aboutOther: 'Цькують іншу людину',
      pick: 'Оберіть звернення зі списку.',
      privacy:
        'Хто написав анонімно, не знає ніхто — навіть система не показує цього ні вам, ні адміністрації. Учень може назватися сам.',
      directorOnly:
        'Тут — повідомлення учнів про булінг і скарги на повідомлення в чатах. Інші звернення бачить лише психолог.',
      mood: 'Настрій класів',
      moodHint:
        'Лише класи, де відповіли щонайменше 5 учнів. Окремих відповідей і того, хто відповів, не бачить ніхто.',
      week: 'Цей тиждень',
      lastWeek: 'Минулий тиждень',
      hard: 'важкі дні',
      respondents_one: 'відповів {{count}} учень',
      respondents_few: 'відповіли {{count}} учні',
      respondents_many: 'відповіли {{count}} учнів',
      respondents_other: 'відповіли {{count}} учня',
      noMood: 'Поки замало відповідей, щоб показати картину.',
      lastWeekWas: 'минулого тижня — {{percent}}%',
      meetings: 'Мої зустрічі',
      homeTitle: 'Турбота сьогодні',
      newCount_one: '{{count}} нове звернення',
      newCount_few: '{{count}} нові звернення',
      newCount_many: '{{count}} нових звернень',
      newCount_other: '{{count}} нового звернення',
      urgentCount_one: '{{count}} термінове',
      urgentCount_few: '{{count}} термінові',
      urgentCount_many: '{{count}} термінових',
      urgentCount_other: '{{count}} термінового',
      open: 'Відкрити «Турботу»',
    },
  },
  pet: {
    title: 'Питомець класу',
    name: 'Дракоша {{class}}',
    stages: {
      egg: 'Яйце',
      hatched: 'Вилупився!',
      baby: 'Малюк',
      young: 'Дракончик',
      dragon: 'Дракон',
      guardian: 'Дракон-охоронець',
    },
    next: 'До стадії «{{stage}}» — {{percent}}%',
    max: 'Найвища стадія! Тримайте планку разом.',
    how: 'Росте, коли весь клас здає домашні, проходить тести й приходить на уроки. Хто скільки зробив — не видно: це спільна справа.',
    week: 'Цього тижня',
    weekPoints: '+{{count}} турботи',
    homework: 'ДЗ здали {{percent}}%',
    attendance: 'на уроках {{percent}}%',
    tests_one: '{{count}} тест',
    tests_few: '{{count}} тести',
    tests_many: '{{count}} тестів',
    tests_other: '{{count}} тесту',
  },
  backpack: {
    title: 'Рюкзак {{day}}',
    tomorrow: 'на завтра',
    books: 'Підручники й зошити: {{subjects}}.',
    nothing: 'Нічого особливого — лише підручники й зошити.',
    teacher: 'просить учитель',
  },
  // «Форми 2»: the student's settings — the profile and the avatar
  settings: {
    title: 'Налаштування',
    avatar: 'Аватар',
    avatarNote: 'зберігається на цьому пристрої',
    change: 'Змінити аватар',
    empty: 'Тут поки нічого налаштовувати.',
  },
  letters: {
    title: 'Лист собі в майбутнє',
    hand: 'капсула часу',
    intro:
      'Напиши собі — і запечатай. Прочитати лист можна буде лише в обраний день: ні батьки, ні школа, ні навіть ти — раніше ніяк.',
    placeholder: 'Привіт, майбутній я! Зараз мені… Я мрію… Хочу запитати тебе…',
    when: 'Коли відкрити?',
    nextYear: 'Через рік',
    yearEnd: 'Наприкінці навчального року',
    graduation: 'На випускному',
    seal: 'Запечатати',
    sealed: 'Лист запечатано. Чекай на нього {{date}}.',
    sealedUntil: 'Запечатано до {{date}}',
    written: 'написано {{date}}',
    openedOn: 'відкрито {{date}}',
    discard: 'Викинути',
    empty: 'Тут будуть твої листи.',
    mine: 'Мої листи',
  },
  future: {
    title: 'Моє майбутнє',
    hand: 'вирішуєш ти',
    map: 'Карта інтересів',
    alumni: 'Випускники',
    intro:
      'Що в тебе виходить і що подобається — за оцінками, гуртками, досягненнями. Це лише підказка, а не вирок: вирішуєш ти.',
    introFamily: 'Що в дитини виходить і що їй подобається. Це підказка для розмови, а не вирок.',
    strength: 'Виходить',
    interest: 'Подобається',
    noGrades: 'ще мало оцінок',
    like: 'Мені цікаво',
    liked: 'Цікаво',
    try: 'Спробуй',
    clubs: 'гуртки',
    professions: 'професії',
    lab: 'лабораторія',
    ask: 'Запитати',
    askPlaceholder: 'Що б ти хотів(-ла) дізнатися?',
    send: 'Надіслати',
    sent: 'Запитання надіслано — відповідь з’явиться тут.',
    mine: 'Мої запитання',
    waiting: 'чекає на відповідь',
    answers: 'Відповіді',
    askedBy: 'запитання з {{class}}',
    graduated: 'випуск {{year}}',
    alumniIntro:
      'Наші випускники розповідають про професії й навчання. Постав запитання — школа передасть його й запише відповідь.',
    toAnswer: 'Запитання до випускників',
    answer: 'Відповідь випускника',
    save: 'Зберегти відповідь',
    noQuestions: 'Нових запитань немає.',
  },
  lessonPack: {
    title: 'Пакет уроку',
    open: 'Пакет уроку',
    missed: 'Ти пропустив(-ла) цей урок — тут усе, щоб наздогнати.',
    missedFamily: 'Дитина пропустила цей урок — тут усе, щоб наздогнати.',
    bring: 'Що принести',
    materials: 'Матеріали',
    boards: 'Дошка уроку',
    homework: 'Домашнє завдання',
    due: 'до {{date}}',
    criteria: 'Критерії',
    test: 'Скоро тест',
    empty: 'Учитель ще нічого не додав до цього уроку.',
    noTopic: 'Тему ще не записано',
    done: 'Розібрався',
    markDone: 'Я розібрався(-лась)',
    sim: 'симуляція',
    file: 'файл',
    link: 'посилання',
    notFound: 'Урок не знайдено.',
  },
  classBoard: {
    title: 'Дошка в класі',
    open: 'Дошка',
    lead: 'Дошка в класі',
    pick: 'Обрати дошку',
    fullscreen: 'На весь екран',
    students_one: '{{count}} учень на дошці',
    students_few: '{{count}} учні на дошці',
    students_many: '{{count}} учнів на дошці',
    students_other: '{{count}} учня на дошці',
    waitingTeacher: 'Оберіть дошку — проєктор покаже її класу, а учні побачать на своїх телефонах.',
    waitingStudent: 'Чекаємо, поки вчитель відкриє дошку…',
    remoteHint:
      'Пульт: відкрийте цей урок у застосунку на телефоні → «Дошка». Що ви малюєте й показуєте з телефона, з’являється на проєкторі.',
    remoteMode: 'Ви ведете дошку з телефона. Сама дошка відкрита на комп’ютері класу.',
    noHost: 'Відкрийте дошку на комп’ютері класу — і ведіть її звідси.',
    leave: 'Вийти',
    demoNow: 'Демо: урок у класі зараз',
  },
  moving: {
    title: 'Переїзд без втрат',
    hand: 'з вашої згоди',
    intro:
      'Якщо дитина переходить до іншої школи, її шкільне життя піде з нею. Ви обираєте, що передати, — і нічого зайвого: чати, «Турбота» й записи вчителів не передаються ніколи.',
    always:
      'Ім’я, дата народження й клас передаються завжди — щоб нова школа знала, про кого йдеться.',
    choose: 'Що передати',
    section: {
      grades: 'Оцінки цього року',
      achievements: 'Досягнення',
      clubs: 'Гуртки',
      attendance: 'Відвідуваність',
      yearbook: 'Альбом року',
    },
    sectionHint: {
      grades: 'середні й семестрові з кожного предмета',
      achievements: 'олімпіади, конкурси, спорт',
      clubs: 'де дитина займається',
      attendance: 'скільки уроків і пропусків (тривоги не рахуються)',
      yearbook: 'найкращі роботи, ДЗ і тести за рік, сезон класу — без фото',
    },
    preview: 'Так виглядатиме паспорт',
    make: 'Створити код для нової школи',
    code: 'Код для нової школи',
    codeHint:
      'Передайте код адміністрації нової школи. Код діє до {{date}} і відкривається один раз. Новий паспорт скасовує попередній код.',
    print: 'Зберегти PDF',
    download: 'Завантажити файл',
    history: 'Створені паспорти',
    status: {
      waiting: 'Код чекає',
      opened: 'Відкрила нова школа',
      cancelled: 'Скасовано',
      expired: 'Минув строк',
    },
    cancel: 'Скасувати код',
    made: 'створено {{date}}',
    openedAt: 'відкрито {{date}}',
    passport: 'Шкільний паспорт',
    born: 'Дата народження',
    class: 'Клас',
    school: 'Школа',
    year: 'Навчальний рік',
    subject: 'Предмет',
    average: 'Середній',
    semester: '{{n}} семестр',
    yearGrade: 'Рік',
    lessons: 'Уроків',
    absent: 'Пропущено',
    excused: 'Через тривогу чи світло',
    none: 'Немає',
    best: 'Найкращі роботи',
    homeworkDone: 'Здано домашніх завдань',
    quizzesDone: 'Тестів: {{count}}, середній результат {{percent}}%',
    puzzlesDone: 'Загадок дня: {{days}}, розгадано {{right}}',
    seasonPart: 'Сезон класу «{{title}}»: виконано частин — {{parts}}',
    footer:
      'Сформовано в «Parta» {{date}} на прохання батьків. Це копія станом на цю дату; офіційні документи (особова справа, табель) видає школа.',
    claimTitle: 'Прийняти учня з іншої школи',
    claimHint: 'Батьки нового учня дають код із 10 символів. Код відкривається один раз.',
    claimPlaceholder: 'XXXX-XXXX-XX',
    claim: 'Відкрити паспорт',
    claimError: 'Код не підійшов: його вже використали, скасували або минув строк.',
    schoolTitle: 'Паспорти учнів нашої школи',
    schoolHint: 'Батьки створюють паспорт самі; школа бачить, коли й що передано.',
    madeBy: 'створив(-ла) {{name}}',
  },
  rewards: {
    title: 'Магазин нагород',
    hand: 'за старання',
    sparks_one: '{{count}} іскра',
    sparks_few: '{{count}} іскри',
    sparks_many: '{{count}} іскор',
    sparks_other: '{{count}} іскри',
    totals: 'зароблено {{earned}} · витрачено {{spent}}',
    intro:
      'Іскри — за старання, а не за оцінки. Оцінку купити не можна, рейтингу немає: іскри бачиш тільки ти й твоя родина.',
    introFamily:
      'Іскри дитина отримує за старання, а не за оцінки. Оцінку купити не можна, рейтингу немає: іскри бачите тільки ви й дитина. Купує й вдягає нагороди сама дитина.',
    how: 'Як заробити',
    source: {
      homework: 'ДЗ здано вчасно',
      puzzle: 'Загадка дня',
      review: 'Картка повторення',
      season: 'Своя частина сезону класу',
      catchUp: 'Розібрався з пропущеним уроком',
      nmt: 'Тренування НМТ',
    },
    history: 'За що іскри',
    noHistory: 'Іскри з’являться за перше ДЗ вчасно чи загадку дня.',
    showAll: 'Показати все',
    pill: '{{count}}',
    station: 'станція {{n}}',
    slot: {
      frame: 'Рамка для аватарки',
      sky: 'Небо на головній',
      buddy: 'Друг біля аватарки',
    },
    buy: 'Купити',
    wear: 'Вдягнути',
    worn: 'Зараз',
    takeOff: 'Зняти',
    short_one: 'ще {{count}} іскра',
    short_few: 'ще {{count}} іскри',
    short_many: 'ще {{count}} іскор',
    short_other: 'ще {{count}} іскри',
    errors: {
      sparks: 'Не вистачає іскор.',
      other: 'Не вийшло. Спробуйте ще раз.',
    },
  },
  forms: {
    title: 'Згоди й форми',
    hand: 'підпис за хвилину',
    intro:
      'Екскурсії, поїздки, заходи класу: погодьте в застосунку — без паперових бланків у рюкзаку.',
    introStaff:
      'Форма на клас — батьки погоджують у застосунку й підписують своїм прізвищем. Видно, хто відповів, і можна нагадати решті.',
    kind: {
      excursion: 'Екскурсія',
      trip: 'Поїздка',
      event: 'Захід',
      other: 'Інше',
    },
    when: 'Коли',
    place: 'Де',
    cost: 'Вартість',
    deadline: 'Відповісти до {{date}}',
    closed: 'Прийом відповідей закрито {{date}}',
    both: 'Потрібна згода обох батьків',
    status: {
      agreed: 'Погоджено',
      refused: 'Не погоджено',
      waiting: 'Чекає відповіді',
      partly: 'Погодив один із батьків',
      abroad: 'За кордоном',
    },
    agree: 'Погоджую',
    disagree: 'Не погоджую',
    sign: 'Підпис',
    signHint: 'Ваше прізвище та ім’я — це підпис під відповіддю',
    signPlaceholder: 'Прізвище Ім’я',
    note: 'Примітка',
    yourAnswer: 'Ви: {{answer}} · {{name}}, {{time}}',
    otherAnswer: '{{name}}: {{answer}}',
    change: 'Змінити відповідь',
    errors: {
      signature: 'Підпис має містити ваше прізвище.',
      deadline: 'Строк відповіді минув.',
      other: 'Не вдалося надіслати. Спробуйте ще раз.',
    },
    studentWaiting: 'Чекаємо на відповідь батьків',
    empty: 'Форм поки немає.',
    newForm: 'Нова форма',
    class: 'Клас',
    formTitle: 'Назва',
    formTitlePlaceholder: 'Екскурсія до археологічного музею',
    details: 'Подробиці',
    detailsPlaceholder: 'Маршрут, транспорт, що взяти з собою, коли повернемося',
    startsOn: 'Дата',
    endsOn: 'До (для поїздки на кілька днів)',
    atTime: 'Час',
    notePrompt: 'Запитання до батьків',
    notePromptPlaceholder: 'Алергії, ліки, телефон для зв’язку',
    create: 'Надіслати батькам',
    remove: 'Видалити',
    summary: '{{agreed}} погодили · {{refused}} проти · {{waiting}} чекаємо',
    abroadNote_one: '{{count}} учень за кордоном — не питаємо',
    abroadNote_few: '{{count}} учні за кордоном — не питаємо',
    abroadNote_many: '{{count}} учнів за кордоном — не питаємо',
    abroadNote_other: '{{count}} учня за кордоном — не питаємо',
    remind: 'Нагадати тим, хто не відповів',
    reminded: 'Нагадування надіслано ({{count}})',
    remindedNone: 'Нагадувати нікому — всі відповіли',
    roster: 'Хто відповів',
    hideRoster: 'Сховати список',
    card_one: 'Потрібен ваш підпис: {{count}} форма',
    card_few: 'Потрібен ваш підпис: {{count}} форми',
    card_many: 'Потрібен ваш підпис: {{count}} форм',
    card_other: 'Потрібен ваш підпис: {{count}} форми',
  },
  abroad: {
    title: 'Навчання з-за кордону',
    hand: 'звідусіль',
    titleSchool: 'Учні за кордоном',
    handSchool: 'школа поруч',
    nowYou: 'У тебе зараз {{time}}',
    nowFamily: 'У вас зараз {{time}}',
    nowSchool: 'у школі {{time}}',
    zoneBehind: 'Київ попереду на {{hours}} год',
    zoneAhead: 'Ви попереду Києва на {{hours}} год',
    zoneSame: 'Час як у Києві',
    track: {
      full: 'Дистанційна форма — усі предмети',
      ukrainian: 'Українознавчий компонент',
    },
    trackHint: {
      full: 'Усі предмети — у нашій школі, дистанційно.',
      ukrainian:
        'Українська мова й література, історія та географія України — у нашій школі; решта предметів — у школі країни, де ви живете.',
    },
    subjects: 'Предмети в нашій школі',
    extra: 'додатково',
    week: 'Тиждень за вашим часом',
    weekHint:
      'Уроки, які припадають на ніч або на місцеву школу, вивчаються за матеріалами й завданнями — учителі про це знають. Онлайн-урок у зручний час можна відкрити з розкладу.',
    fit: {
      live: 'зручний час',
      school: 'місцева школа',
      night: 'у вас ніч',
    },
    liveCount_one: '{{count}} урок у зручний час',
    liveCount_few: '{{count}} уроки у зручний час',
    liveCount_many: '{{count}} уроків у зручний час',
    liveCount_other: '{{count}} уроку у зручний час',
    asyncCount_one: '{{count}} — за матеріалами',
    asyncCount_few: '{{count}} — за матеріалами',
    asyncCount_many: '{{count}} — за матеріалами',
    asyncCount_other: '{{count}} — за матеріалами',
    kyiv: 'Київ {{time}}',
    materials: 'Матеріали',
    noLessons: 'Цього тижня уроків немає.',
    home: 'Де ви живете',
    homeHint:
      'Сповіщення не приходять уночі за вашим часом, а тривога в школі не будить вас — повідомимо, коли онлайн-уроки стануть на паузу.',
    country: 'Країна',
    city: 'Місто',
    zone: 'Часовий пояс',
    localSchool: 'Ходить до місцевої школи',
    localFrom: 'з',
    localTo: 'до',
    save: 'Зберегти',
    saved: 'Збережено',
    nmt: {
      title: 'НМТ за кордоном',
      text: 'НМТ можна скласти в іншій країні: у 2026 році пункти тестування працювали більш ніж у 30 країнах. Реєстрація — навесні, а місць у містах за кордоном небагато: вони закінчуються в перші дні, тож обирайте місто одразу.',
      practice: 'Тренуватися',
    },
    links: {
      title: 'Корисне',
      mon: 'МОН: освіта для дітей за кордоном',
      studies: 'Заява на українознавчий компонент',
      nmt: 'УЦОЯО: реєстрація на НМТ',
    },
    card: 'Навчаєшся з-за кордону',
    cardFamily: 'Навчається з-за кордону',
    cardToday_one: 'сьогодні {{count}} урок у зручний час',
    cardToday_few: 'сьогодні {{count}} уроки у зручний час',
    cardToday_many: 'сьогодні {{count}} уроків у зручний час',
    cardToday_other: 'сьогодні {{count}} уроку у зручний час',
    cardNoLive: 'сьогодні всі уроки — за матеріалами',
    students_one: '{{count}} учень',
    students_few: '{{count}} учні',
    students_many: '{{count}} учнів',
    students_other: '{{count}} учня',
    countries_one: '{{count}} країна',
    countries_few: '{{count}} країни',
    countries_many: '{{count}} країн',
    countries_other: '{{count}} країни',
    schoolIntro:
      'Діти, які живуть за кордоном і навчаються в нашій школі дистанційно. Форму навчання встановлює школа (за заявою батьків), а країну, місто й години місцевої школи родина змінює сама.',
    add: 'Додати учня',
    edit: 'Змінити',
    remove: 'Прибрати',
    removeAsk: 'Учень повертається до звичайного навчання?',
    student: 'Учень',
    pickStudent: 'Оберіть учня',
    search: 'Прізвище або ім’я',
    form: 'Форма навчання',
    since: 'з {{date}}',
    empty: 'Учнів за кордоном немає.',
    there: 'там {{time}}',
    nightNow: 'там ніч',
    journalHint: 'Навчається з-за кордону: {{place}} · {{time}}',
    asyncCell: 'дист.',
    legend: 'дист. — урок припадає на ніч чи місцеву школу учня за кордоном',
    asyncTitle: 'Урок припадає на {{why}} учня — тему він вивчає за матеріалами',
    asyncWhy: {
      school: 'місцеву школу',
      night: 'ніч',
    },
    rollCall: '{{names}} — за кордоном, у перекличці не беруть участі',
  },
  yearbook: {
    title: 'Альбом року',
    hand: 'мій {{year}}',
    building: 'Альбом збирається протягом року — у червні він буде готовий.',
    cover: '{{class}} · {{year}}',
    numbers: 'Мій рік у цифрах',
    lessons: 'уроків',
    attendance: 'відвідуваність',
    average: 'середній бал',
    homework: 'домашніх здано',
    quizzes: 'тестів',
    puzzles: 'загадок розгадано',
    best: 'Найкращі роботи',
    noBest: 'Найкращі роботи з’являться тут.',
    subjects: 'Улюблені предмети',
    achievements: 'Досягнення',
    noAchievements: 'Досягнення року з’являться тут.',
    clubs: 'Гуртки',
    season: 'Сезон класу',
    seasonLine: '{{title}}: станцій пройдено — {{stage}} з {{stages}}, моя роль — {{role}}',
    photos: 'Фото класу',
    letters_one: 'Цього року ти написав(-ла) {{count}} лист у майбутнє.',
    letters_few: 'Цього року ти написав(-ла) {{count}} листи в майбутнє.',
    letters_many: 'Цього року ти написав(-ла) {{count}} листів у майбутнє.',
    letters_other: 'Цього року ти написав(-ла) {{count}} листа в майбутнє.',
    save: 'Зберегти PDF',
    prev: '← Назад',
    next: 'Далі →',
    page: 'Сторінка {{n}} з {{total}}',
  },
  season: {
    title: 'Сезон класу',
    hand: 'разом до мети',
    station: 'Станція {{n}} з {{total}}',
    yourRole: 'Твоя роль',
    mission: 'Місія станції',
    yours: 'твоя команда',
    done: 'Я зробив(-ла) свою частину',
    doneAlready: 'Твоя частина готова',
    moved: 'Клас рушив далі!',
    team: 'Команди класу',
    progress: '{{done}} з {{total}} уже зробили свою частину',
    rule: 'Коли три чверті класу зроблять свою частину — клас полетить далі. Рейтингу немає: рахуємо тільки разом.',
    none: 'У класу поки немає сезону.',
    start: 'Почати сезон «{{title}}»',
    startHint:
      'Кожен учень отримає роль у команді; станції відкриватимуться для всього класу разом.',
    next: 'Наступна станція',
    finished: 'Сезон пройдено!',
    finishedHint: 'Уся команда дійшла до фіналу.',
    open: 'До сезону',
    noClass: 'Сезон веде класний керівник свого класу.',
  },
  planning: {
    title: 'План уроків',
    hand: 'семестр за 10 хвилин',
    intro:
      'Розкладіть теми на весь семестр: вставте список тем або візьміть зразок програми — уроки заповняться за календарем.',
    course: 'Курс',
    term: 'Семестр',
    termN: '{{n}} семестр',
    source: 'Звідки теми',
    program: 'Зразок програми',
    copyFrom: 'Як у {{class}}',
    topics: 'Теми — по одній у рядку',
    even: 'Рівномірно на всі уроки',
    per: 'По {{n}} уроки на тему',
    preview: 'Розкласти',
    save: 'Зберегти план',
    saved_one: 'Збережено: {{count}} урок',
    saved_few: 'Збережено: {{count}} уроки',
    saved_many: 'Збережено: {{count}} уроків',
    saved_other: 'Збережено: {{count}} уроку',
    reset: 'Скасувати',
    lessons_one: '{{count}} урок у семестрі',
    lessons_few: '{{count}} уроки в семестрі',
    lessons_many: '{{count}} уроків у семестрі',
    lessons_other: '{{count}} уроку в семестрі',
    noTopic: 'без теми',
    changed: 'нова тема',
    details: 'Деталі',
    empty: 'У цій чверті уроків курсу немає.',
    noCourses: 'Курсів, які ви ведете, немає.',
  },
  nmt: {
    title: 'Підготовка до НМТ',
    hand: 'крок за кроком',
    daysLeft_one: 'До НМТ — {{count}} день',
    daysLeft_few: 'До НМТ — {{count}} дні',
    daysLeft_many: 'До НМТ — {{count}} днів',
    daysLeft_other: 'До НМТ — {{count}} дня',
    approx: 'орієнтовно, основна сесія — травень–червень',
    intro:
      'Українська мова й математика — обов’язкові, ще два предмети — на вибір. Пробні тести тут тренувальні, бали — орієнтовні.',
    introFamily: 'Пробні тести дитини і її план повторення. Школа цих результатів не бачить.',
    subjects: 'Предмети',
    required: 'обов’язковий',
    pickTwo: 'Обери ще два предмети',
    start: 'Пробний тест',
    last: 'Останній: ≈{{score}} зі 200',
    never: 'Ще не було спроб',
    attempts_one: '{{count}} спроба',
    attempts_few: '{{count}} спроби',
    attempts_many: '{{count}} спроб',
    attempts_other: '{{count}} спроби',
    plan: 'Мій план',
    planFamily: 'План повторення',
    week: 'Цього тижня повтори',
    planEmpty: 'Пройди пробні тести — план складеться з тем, де були помилки.',
    later: 'Далі: {{topics}}',
    weeks_one: 'До НМТ {{count}} тиждень — по 3 теми на тиждень.',
    weeks_few: 'До НМТ {{count}} тижні — по 3 теми на тиждень.',
    weeks_many: 'До НМТ {{count}} тижнів — по 3 теми на тиждень.',
    weeks_other: 'До НМТ {{count}} тижня — по 3 теми на тиждень.',
    question: 'Питання {{n}} з {{total}}',
    answer: 'Відповісти',
    next: 'Далі',
    finish: 'Результат',
    result: '{{correct}} з {{total}} · ≈{{score}} балів зі 200',
    resultHint: 'Бали орієнтовні: справжня шкала залежить від таблиць року.',
    missed: 'Варто повторити: {{topics}}',
    allRight: 'Жодної помилки — так тримати!',
    again: 'Ще раз',
    close: 'До предметів',
    official: 'Офіційні матеріали й реєстрація — на сайті УЦОЯО',
    notYet: 'Підготовка до НМТ відкривається в 10–11 класах.',
  },
  review: {
    title: 'Повторення',
    intro: '3 питання — перевіримо, що ти пам’ятаєш.',
    of: '{{n}} з {{total}}',
    check: 'Перевірити',
    next: 'Далі',
    finish: 'Готово',
    right: 'Пам’ятаєш!',
    wrong: 'Цього разу ні — повторимо ще.',
    rightAnswer: 'Правильно: {{answer}}',
    later: 'Наступного разу — {{date}}',
    done: 'На сьогодні все повторено.',
    nothing: 'Сьогодні повторювати нічого.',
    nextOn: 'Наступне повторення — {{date}}.',
    empty: 'Повторення з’явиться після твоїх перших тестів.',
    stats: 'Пам’ятаєш {{remembered}} з {{total}} питань',
    pickMany: 'Можна обрати кілька відповідей',
    tree: 'Дерево знань',
  },
  tree: {
    title: 'Дерево знань',
    hand: 'росте з кожною темою',
    intro: 'Теми предметів у порядку уроків: що вже освоєно, що варто повторити і що попереду.',
    summary: 'Освоєно тем: {{done}} · варто повторити: {{weak}}',
    status: {
      done: 'Освоєно',
      weak: 'Варто повторити',
      now: 'Вивчаємо зараз',
      next: 'Попереду',
    },
    lessons: 'уроків {{held}} з {{total}}',
    avg: 'середній бал {{avg}}',
    quiz: 'тести {{pct}}%',
    repeat: 'Як повторити:',
    lab: 'симуляція «{{title}}»',
    materials: 'матеріали уроків',
    empty: 'Тут з’являться теми, щойно почнуться уроки.',
  },
  clubs: {
    title: 'Гуртки',
    hand: 'після уроків',
    intro: 'Запишися в одне касання — заняття з’являться в розкладі.',
    introFamily: 'Запишіть дитину в одне касання — заняття з’являться в її розкладі.',
    introStaff: 'Гуртки школи: хто веде, коли й скільки місць.',
    forClass: 'Для {{n}} класу',
    mine: 'Мої гуртки',
    all: 'Усі',
    grades: '{{from}}–{{to}} клас',
    gradesOne: '{{n}} клас',
    seats: 'Вільних місць: {{count}}',
    noSeats: 'Місць немає',
    anyone: 'Без обмежень',
    join: 'Записатися',
    joined: 'Ти в гуртку',
    leave: 'Вийти',
    pending: 'Чекає згоди батьків',
    cancel: 'Скасувати',
    enrol: 'Записати: {{name}}',
    enrolled: '{{name}} в гуртку',
    unenrol: 'Виписати',
    requests: 'Запити від дітей',
    request: '{{name}} хоче на «{{club}}»',
    approve: 'Погодити',
    decline: 'Відхилити',
    roster: 'Учасники',
    rosterEmpty: 'Поки нікого.',
    waiting: 'чекає згоди батьків',
    leads: 'Ви ведете',
    empty: 'Гуртків поки немає.',
    emptyMine: 'Ти ще не записаний на жоден гурток.',
    today: 'Сьогодні гурток',
    errors: {
      full: 'Місця щойно закінчилися.',
      grade: 'Цей гурток для інших класів.',
      other: 'Не вдалося. Спробуйте ще раз.',
    },
    form: {
      button: 'Новий гурток',
      name: 'Назва',
      emoji: 'Емодзі',
      about: 'Про гурток',
      leader: 'Керівник',
      noLeader: 'Без керівника',
      gradeFrom: 'З класу',
      gradeTo: 'По клас',
      capacity: 'Місць (порожньо — без обмежень)',
      room: 'Де',
      day: 'День',
      start: 'Початок',
      end: 'Кінець',
      addSlot: '＋ ще день',
      save: 'Створити гурток',
    },
    remove: 'Видалити',
    removeSure: 'Точно видалити?',
  },
  lab: {
    title: 'Лабораторія',
    hand: 'можна помилятися',
    intro:
      'Досліди без ризику: збирай електричні кола, змішуй розчини, запускай планети. Помилився — спробуй ще раз.',
    sims: 'Симуляції',
    models: '3D-моделі',
    allSubjects: 'Усі предмети',
    allClasses: 'Усі класи',
    grade: '{{n}} клас',
    empty: 'Для цього предмета й класу поки нічого немає.',
    back: '← Лабораторія',
    tries: 'Спробуй',
    fullscreen: 'На весь екран',
    online: 'Симуляція відкривається з інтернетом.',
    credit:
      'Симуляції — PhET Interactive Simulations, Університет Колорадо в Боулдері, ліцензія CC BY 4.0.',
    notFound: 'Такої симуляції немає.',
    add: {
      title: 'До матеріалів курсу',
      course: 'Курс',
      button: 'Додати',
      done: 'Додано до матеріалів',
    },
    suggest: {
      title: 'До теми є симуляції',
      add: 'Додати до уроку',
      added: 'Додано',
    },
    lesson: {
      button: 'Лабораторія',
      pick: 'Відкрити всім симуляцію',
      forTopic: 'До теми уроку',
      forSubject: 'Для предмета',
      closeForAll: 'Закрити для всіх',
      yours: 'У кожного своя копія — пробуй сам.',
      showVideo: 'Відео',
      showLab: 'Лабораторія',
    },
    model: {
      labels: 'Підписи',
      reset: 'Скинути вигляд',
      ar: 'Поставити на стіл',
      arPreparing: 'Готуємо модель для камери…',
      arElsewhere: 'На iPhone чи iPad модель можна поставити на стіл через камеру (AR).',
      open: 'Відкрити 3D',
    },
    planet: {
      size: 'Діаметр',
      earths: 'Землі',
      distance: 'Від Сонця',
      day: 'Доба',
      year: 'Рік',
      moons: 'Супутники',
      tap: 'Торкнися планети, щоб дізнатися про неї.',
      orbitsNote: 'Відстані стиснуто, планети збільшено. Рік Землі тут триває 10 секунд.',
      sizesNote:
        'Розміри — справжні: ліворуч край Сонця, у нього вмістилося б понад мільйон Земель.',
    },
    atom: {
      protons_one: '{{count}} протон',
      protons_few: '{{count}} протони',
      protons_many: '{{count}} протонів',
      protons_other: '{{count}} протона',
      neutrons_one: '{{count}} нейтрон',
      neutrons_few: '{{count}} нейтрони',
      neutrons_many: '{{count}} нейтронів',
      neutrons_other: '{{count}} нейтрона',
      electrons_one: '{{count}} електрон',
      electrons_few: '{{count}} електрони',
      electrons_many: '{{count}} електронів',
      electrons_other: '{{count}} електрона',
      shells: 'по рівнях:',
      note: 'Спрощена модель Бора. Насправді електрони не бігають колами, а утворюють «хмари».',
    },
    solid: {
      counts: 'Вершин {{v}} · ребер {{e}} · граней {{f}}',
      euler: 'Перевір формулу Ейлера: В − Р + Г = 2.',
      unfold: 'Розгорнути',
      fold: 'Згорнути',
    },
  },
  puzzle: {
    title: 'Загадка дня',
    right: 'Правильно!',
    wrong: 'Цього разу ні.',
    streak_one: '{{count}} день поспіль',
    streak_few: '{{count}} дні поспіль',
    streak_many: '{{count}} днів поспіль',
    streak_other: '{{count}} дня поспіль',
    badge: 'Значок «Мозковий штурм» — 7 днів поспіль!',
    toBadge_one: 'До значка «Мозковий штурм» — ще {{count}} день',
    toBadge_few: 'До значка «Мозковий штурм» — ще {{count}} дні',
    toBadge_many: 'До значка «Мозковий штурм» — ще {{count}} днів',
    toBadge_other: 'До значка «Мозковий штурм» — ще {{count}} дня',
    tomorrow: 'Нова загадка — завтра.',
  },
  catchUp: {
    title: 'Догнати пропущене',
    hint: 'Уроки, пропущені за останні три тижні: про що були, що задали і де матеріали.',
    first: 'Спершу',
    left_one: 'лишився {{count}} урок',
    left_few: 'лишилося {{count}} уроки',
    left_many: 'лишилося {{count}} уроків',
    left_other: 'лишилося {{count}} уроку',
    allDone: 'Усе надолужено — молодець!',
    marks: {
      absent: 'пропуск',
      alarm: 'тривога',
      technical: 'тех. причина',
    },
    noTopic: 'Тему не вказано',
    homework: 'ДЗ: {{title}}, до {{date}}',
    submitted: 'здано',
    materials_one: '{{count}} матеріал',
    materials_few: '{{count}} матеріали',
    materials_many: '{{count}} матеріалів',
    materials_other: '{{count}} матеріалу',
    boards_one: '{{count}} дошка',
    boards_few: '{{count}} дошки',
    boards_many: '{{count}} дощок',
    boards_other: '{{count}} дошки',
    test: 'Скоро тест «{{title}}» — {{date}}',
    done: 'Готово',
    undo: 'Повернути',
    more: 'Показати все',
    less: 'Згорнути',
  },
  shelter: {
    title: 'Завдання в укритті',
    open: 'Завдання на 10 хвилин',
    hint: 'Коротке завдання, поки чекаємо на відбій. Працює без інтернету, нікуди не надсилається.',
    kinds: {
      reading: 'Читаємо',
      puzzle: 'Задачки',
    },
    minutes_one: '≈ {{count}} хвилина',
    minutes_few: '≈ {{count}} хвилини',
    minutes_many: '≈ {{count}} хвилин',
    minutes_other: '≈ {{count}} хвилини',
    score: '{{right}} з {{total}} —',
    praiseAll: 'чудово!',
    praiseSome: 'гарна спроба!',
    another: 'Ще одне завдання',
  },
  chats: {
    title: 'Повідомлення',
    new: 'Нове повідомлення',
    search: 'Пошук за прізвищем',
    noContacts: 'Нікого не знайдено.',
    empty: 'Розмов поки немає.',
    pick: 'Оберіть розмову ліворуч.',
    placeholder: 'Напишіть повідомлення…',
    send: 'Надіслати',
    you: 'Ви',
    classParents: 'Чат батьків {{class}}',
    quietHours:
      'Зараз тихі години (21:00–08:00): сповіщення про ваше повідомлення прийде вчителю зранку.',
  },
  admin: {
    title: 'Адміністрування',
    onlyAdmin: 'Розділ доступний адміністрації школи.',
    tabs: { people: 'Люди', classes: 'Класи', timetable: 'Розклад', audit: 'Журнал дій' },
    people: {
      search: 'Пошук за прізвищем або класом',
      all: 'Усі',
      add: 'Додати людину',
      count_one: '{{count}} людина',
      count_few: '{{count}} людини',
      count_many: '{{count}} людей',
      count_other: '{{count}} людини',
      hasAccount: 'має доступ',
      invite: 'Запросити',
      lastName: 'Прізвище',
      firstName: 'Імʼя',
      middleName: 'По батькові',
      birthDate: 'Дата народження',
      child: 'Дитина',
      save: 'Зберегти',
    },
    invite: {
      title: 'Запрошення',
      hint: 'Відскануйте QR-код телефоном або введіть код на сторінці «Маю код запрошення».',
      validUntil: 'Діє до {{date}}',
      print: 'Друкувати',
    },
    import: {
      title: 'Імпорт CSV',
      format:
        'Один рядок — одна людина: тип; прізвище; імʼя; по батькові; дата народження (РРРР-ММ-ДД); клас. Тип: учень, батьки або працівник.',
      ready_one: 'Готово до імпорту: {{count}} рядок',
      ready_few: 'Готово до імпорту: {{count}} рядки',
      ready_many: 'Готово до імпорту: {{count}} рядків',
      ready_other: 'Готово до імпорту: {{count}} рядка',
      bad_one: '{{count}} рядок з помилкою',
      bad_few: '{{count}} рядки з помилками',
      bad_many: '{{count}} рядків з помилками',
      bad_other: '{{count}} рядка з помилками',
      line: 'Рядок {{line}}',
      errors: {
        unknown_kind: 'невідомий тип (учень, батьки, працівник)',
        name_missing: 'немає прізвища або імені',
        bad_date: 'дата має бути у форматі РРРР-ММ-ДД',
        unknown_class: 'такого класу немає',
      },
      run: 'Імпортувати',
      done_one: 'Додано {{count}} людину',
      done_few: 'Додано {{count}} людини',
      done_many: 'Додано {{count}} людей',
      done_other: 'Додано {{count}} людини',
    },
    classes: { homeroom: 'Класний керівник' },
    timetable: {
      add: 'Додати в розклад',
      generate: 'Створити уроки за розкладом',
      generated_one: 'Створено {{count}} урок',
      generated_few: 'Створено {{count}} уроки',
      generated_many: 'Створено {{count}} уроків',
      generated_other: 'Створено {{count}} уроку',
    },
    audit: {
      system: 'Система',
      actions: { insert: 'додано', update: 'змінено', delete: 'видалено' },
      entities: {
        school_members: 'людину',
        staff_roles: 'роль працівника',
        guardianships: 'звʼязок батьків і дитини',
        invites: 'запрошення',
        lessons: 'урок',
        timetable_slots: 'розклад',
        school_days_off: 'вихідний день',
        announcements: 'оголошення',
        alarm_sessions: 'тривогу',
        shelters: 'укриття',
      },
    },
  },
  stickers: {
    star: 'Зірочка',
    progress: 'Прогрес',
    effort: 'Старанність',
    idea: 'Ідея',
    help: 'Допомога',
  },
  roles: {
    student: 'Учень',
    guardian: 'Батьки',
    staff: 'Працівник',
    teacher: 'Вчитель',
    admin: 'Адміністрація',
    director: 'Директор',
    deputy: 'Заступник директора',
    psychologist: 'Психолог',
  },
  home: {
    greeting: 'Вітаємо!',
    hello: 'Вітаємо, {{name}}!',
    // «Форми 2»: the one handwritten line over the name on the homes of the grown-ups
    hi: 'вітаємо,',
    yourSchools: 'Ваші школи',
    noMemberships: 'Вас ще не додано до жодної школи. Зверніться до адміністрації школи.',
    today: 'Сьогодні',
    nextDay: 'Найближчі уроки — {{day}}',
    noLessonsSoon: 'Найближчим тижнем уроків немає.',
    latestGrades: 'Нові оцінки',
    allGrades: 'Усі оцінки',
    fullSchedule: 'Весь розклад',
    openJournal: 'Журнал',
    absencesTerm: 'Пропуски в семестрі',
    average: 'Середній бал',
    schoolToday: 'Школа сьогодні',
    schoolOn: 'Школа · {{day}}',
    changesCount_one: '{{count}} зміна в розкладі',
    changesCount_few: '{{count}} зміни в розкладі',
    changesCount_many: '{{count}} змін у розкладі',
    changesCount_other: '{{count}} зміни в розкладі',
    lessonsToday: 'Уроків',
    online: 'Онлайн',
    shelter: 'В укритті',
    cancelled: 'Скасовано',
    substitutions: 'Заміни',
    absentToday: 'Відсутні учні',
    changes: 'Зміни в розкладі',
    noChanges: 'Змін немає — усе за розкладом.',
    homeroom: 'Мій клас {{class}}',
  },
  // bug reports of the team testing the site and the app (29.10)
  bugs: {
    button: 'Повідомити про помилку',
    title: 'Повідомити про помилку',
    lead: 'Опишіть, що пішло не так: що ви робили і що побачили. Сторінку, роль і пристрій ми додамо самі.',
    placeholder: 'Наприклад: натиснув «Увійти» на уроці алгебри — і сторінка стала порожньою',
    screenshots: 'Скриншоти',
    add: 'Додати скриншот',
    paste: 'Можна вставити скриншот із буфера: Ctrl+V або ⌘V',
    limit: 'До 6 скриншотів, кожен до 5 МБ',
    remove: 'Прибрати скриншот',
    send: 'Надіслати',
    sending: 'Надсилаємо…',
    sent: 'Дякуємо! Повідомлення отримали — подивимося й виправимо.',
    another: 'Надіслати ще одне',
    failed: 'Не вдалося надіслати. Перевірте звʼязок і спробуйте ще раз.',
    tooBig: 'Скриншот завеликий — до 5 МБ.',
  },
  // the student's home of design v4
  studentHome: {
    hi: 'привіт,',
    cancelled: 'Скасовано',
    substitute: 'Заміна',
    test: 'Контрольна',
    online: 'Онлайн',
    shelter: 'В укритті',
    now: 'зараз · ще {{min}} хв',
    onlyBooks: 'лише підручники й зошити',
    stats: {
      sparks: 'Іскри',
    },
    // the greeting: where the day stands, one sentence about what matters now, a line of details
    head: {
      chip: {
        before: 'До уроків {{min}} хв',
        today: 'Сьогодні {{lessons}}, {{from}}–{{to}}',
        live: 'Урок іде · ще {{min}} хв',
        break: 'Перерва · ще {{min}} хв',
        over: 'Уроки закінчилися о {{time}}',
        free: 'Сьогодні уроків немає',
      },
      first: 'Перший урок о {{time}} — {{subject}}{{place}}.',
      live: 'Зараз {{subject}}{{place}}.',
      break: 'Далі — {{subject}} о {{time}}{{place}}.',
      free: 'Наступні уроки {{day}}, з {{time}}.',
      none: 'Найближчим часом уроків немає.',
      dueOnMain: 'На {{day}} — {{tasks}}.',
      nothingDueMain: 'На {{day}} завдань немає.',
      // the quiet line under the sentence
      then: 'Далі — {{subject}} о {{time}}{{place}}',
      last: 'Це останній урок на сьогодні',
      left: 'Ще {{lessons}}, до {{time}}',
      testToday: 'Сьогодні контрольна: {{subjects}}',
      testOn: '{{day}} контрольна: {{subjects}}',
      dueOn: 'На {{day}} — {{tasks}}',
      nothingDue: 'На {{day}} завдань немає',
      overdue: 'Прострочено {{tasks}}',
      tasks_one: '{{count}} завдання',
      tasks_few: '{{count}} завдання',
      tasks_many: '{{count}} завдань',
      tasks_other: '{{count}} завдання',
      tomorrow: 'завтра',
      tomorrowCap: 'Завтра',
      online: 'онлайн',
    },
    // the day on the home: today, after the lessons the next school day
    day: {
      title: 'Уроки',
      today: 'Сьогодні',
      tomorrow: 'Завтра',
      weekButton: 'Розклад тижня',
      nothing: 'Найближчі два тижні уроків немає.',
      clock: 'Зараз {{time}}',
      nextIn: 'за {{min}} хв',
      beforeFull: 'До першого уроку {{min}} хв',
      over: 'Уроки закінчилися о {{time}}',
      break: 'перерва {{min}} хв',
      bigBreak: 'велика перерва {{min}} хв',
      window: 'вікно {{min}} хв',
      breakGoesOn: 'Зараз перерва · ще {{min}} хв',
      more_one: 'ще {{count}} урок',
      more_few: 'ще {{count}} уроки',
      more_many: 'ще {{count}} уроків',
      more_other: 'ще {{count}} уроку',
      pack: 'Взяти з собою',
      period: '{{n}}-й урок',
      onlineLesson: 'Онлайн-урок',
      substituteTeacher: 'Заміна',
      topic: 'Тема',
      bring: 'Принести',
      homeworkDue: 'Здати на цей урок',
      homeworkGiven: 'Задали на цьому уроці',
      handed: 'здано',
      notHanded: 'не здано',
    },
    // what to do, as cards by urgency: the big number asks for action
    cards: {
      open: 'Відкрити розділ',
      overdue: {
        title: 'Прострочено',
        caption_one: 'завдання після терміну',
        caption_few: 'завдання після терміну',
        caption_many: 'завдань після терміну',
        caption_other: 'завдання після терміну',
      },
      soon: {
        title: 'Найближчі завдання',
        today_one: 'завдання на сьогодні',
        today_few: 'завдання на сьогодні',
        today_many: 'завдань на сьогодні',
        today_other: 'завдання на сьогодні',
        tomorrow_one: 'завдання на завтра',
        tomorrow_few: 'завдання на завтра',
        tomorrow_many: 'завдань на завтра',
        tomorrow_other: 'завдання на завтра',
        week_one: 'завдання цього тижня',
        week_few: 'завдання цього тижня',
        week_many: 'завдань цього тижня',
        week_other: 'завдання цього тижня',
      },
      tests: {
        title: 'Контрольні тижня',
        caption_one: 'контрольна цього тижня',
        caption_few: 'контрольні цього тижня',
        caption_many: 'контрольних цього тижня',
        caption_other: 'контрольної цього тижня',
      },
      rework: {
        title: 'Переробити',
        caption_one: 'роботу повернули',
        caption_few: 'роботи повернули',
        caption_many: 'робіт повернули',
        caption_other: 'роботи повернули',
      },
      ack: {
        title: 'Підтвердити',
        caption_one: 'оголошення чекає',
        caption_few: 'оголошення чекають',
        caption_many: 'оголошень чекають',
        caption_other: 'оголошення чекають',
      },
      catchUp: {
        title: 'Наздогнати',
        caption_one: 'пропущений урок',
        caption_few: 'пропущені уроки',
        caption_many: 'пропущених уроків',
        caption_other: 'пропущеного уроку',
        hint: 'Теми, домашнє й матеріали',
      },
      done: {
        title: 'Усе зроблено',
        text: 'Нічого термінового. Нові завдання зʼявляться тут.',
      },
    },
    // «Скло»: the fourth look (refs «Aella Credit» for the glass, «Duomo» for the pictures)
    glass: {
      prev: 'Назад',
      next: 'Далі',
      todo: 'Треба зробити',
      all: 'Усі завдання',
      grades: 'Останні оцінки',
      allGrades: 'Усі оцінки',
      noGrades: 'Оцінок цього семестру ще немає',
      // the greeting's card: what goes on now and what comes next
      now: {
        now: 'Зараз',
        next: 'Далі',
        first: 'Перший урок',
        tomorrow: 'Завтра',
        break: 'Перерва',
        beforeLessons: 'До уроків',
        left: 'ще {{min}} хв',
        last: 'Це останній урок сьогодні',
        over: 'Уроки закінчилися',
        weekend: 'Вихідний',
        until: 'до {{date}}',
        lessons: '{{lessons}}, {{from}}–{{to}}',
        test: 'контрольна',
        noLessons: 'Найближчим часом уроків немає',
      },
      // the lessons as a calendar: the days of the week on top
      week: {
        title: 'Розклад',
        back: 'Сьогодні',
        prev: 'Попередній тиждень',
        next: 'Наступний тиждень',
        all: 'Розклад тижня',
        summary: '{{lessons}}, {{from}}–{{to}}',
        none: 'Уроків немає',
        weekend: 'Вихідний',
        nowBreak: 'зараз · перерва, ще {{min}} хв',
        nowBefore: 'зараз · до уроків {{min}} хв',
        nowOver: 'уроки закінчилися о {{time}}',
        left: 'ще {{min}} хв',
        in: 'за {{min}} хв',
        club: 'Гурток',
        pack: 'Взяти',
        nextDay: 'Найближчий урок — {{day}}',
      },
      newGrades: 'Нові оцінки',
      fresh: 'нова',
      // what to do: a card per kind, the tasks inside
      board: {
        overdue: 'Прострочено',
        soon: 'Найближчі',
        tests: 'Контрольні',
        rework: 'Переробити',
        ack: 'Підтвердити',
        catchUp: 'Наздогнати',
        done: 'Усе зроблено',
        doneText: 'Нічого термінового — можна видихнути.',
        more: 'ще {{count}}',
      },
      // the school: fresh news for the students and the events of two weeks
      school: {
        title: 'У школі',
        all: 'Усі події',
        news: 'Нове',
        empty: 'Найближчі два тижні подій немає',
        today: 'сьогодні',
        tomorrow: 'завтра',
        yesterday: 'вчора',
        in_one: 'через {{count}} день',
        in_few: 'через {{count}} дні',
        in_many: 'через {{count}} днів',
        in_other: 'через {{count}} дня',
        goesOn: 'триває до {{date}}',
      },
      mood: {
        hint: 'Відмітка на сьогодні, один дотик',
        week: 'Мій тиждень',
        change: 'Змінити',
      },
    },
    // «Скло 2»: the profile header, the mood in a button, the blocks of the moment
    glass2: {
      classOf: '{{label}} клас',
      cover: 'Змінити обкладинку',
      coverTitle: 'Обкладинка',
      covers: {
        wave: 'Хвиля',
        sunrise: 'Світанок',
        notebook: 'Зошит',
        space: 'Космос',
        meadow: 'Луг',
      },
      decorate: 'Прикрасити профіль у «Магазині нагород»',
      club: 'Гурток',
      birthday: 'День народження {{date}}',
      birthdayToday: 'З днем народження!',
      mood: {
        ask: 'Як ти?',
        title: 'Як ти сьогодні?',
        week: 'Мій тиждень',
        more: 'Детальніше в «Турботі»',
      },
      notice: {
        label: 'Важливо',
        ack: 'Підтвердити',
        open: 'Детальніше',
        more_one: 'І ще {{count}} важливе оголошення',
        more_few: 'І ще {{count}} важливі оголошення',
        more_many: 'І ще {{count}} важливих оголошень',
        more_other: 'І ще {{count}} важливого оголошення',
      },
      grades: {
        more_one: 'ще {{count}} нова',
        more_few: 'ще {{count}} нові',
        more_many: 'ще {{count}} нових',
        more_other: 'ще {{count}} нової',
      },
      // the situations shown on the preview only (`?sim=notice`)
      sim: {
        noticeTitle: 'Завтра о 18:00 — батьківські збори 7-Б, присутність обовʼязкова',
        noticeAuthor: 'Класний керівник',
      },
    },
    // «Форми 2»: the small greeting
    shapes2: {
      hi: 'привіт,',
      avatar: 'Змінити аватар',
      avatarTitle: 'Обери аватар',
      initials: 'Ініціали',
      less: 'Згорнути',
      calendar: 'Календар',
      allDone: 'Усе здано',
      allSubjects: 'Усі',
      due: {
        late: 'Прострочено',
        today: 'Сьогодні',
        tomorrow: 'Завтра',
        week: 'Цього тижня',
        later: 'Пізніше',
      },
      lateCount_one: '{{count}} прострочене',
      lateCount_few: '{{count}} прострочені',
      lateCount_many: '{{count}} прострочених',
      lateCount_other: '{{count}} простроченого',
      where: 'Де',
      teacher: 'Учитель',
      prevMonth: 'Попередній місяць',
      nextMonth: 'Наступний місяць',
      legendTest: 'контрольна',
      legendEvent: 'подія',
      legendHoliday: 'канікули',
      ahead: 'Попереду',
      aheadNone: 'Контрольних і подій попереду немає',
      moreTasks_one: 'ще {{count}}',
      moreTasks_few: 'ще {{count}}',
      moreTasks_many: 'ще {{count}}',
      moreTasks_other: 'ще {{count}}',
      daysLeft_one: 'ще {{count}} день',
      daysLeft_few: 'ще {{count}} дні',
      daysLeft_many: 'ще {{count}} днів',
      daysLeft_other: 'ще {{count}} дня',
    },
    // «Форми»: the headline of the day
    shapes: {
      hi: 'Привіт,',
      today: 'Сьогодні',
      tomorrow: 'Завтра',
      fromTo: 'з {{from}} до {{to}}',
      tests_one: '{{count}} контрольна',
      tests_few: '{{count}} контрольні',
      tests_many: '{{count}} контрольних',
      tests_other: '{{count}} контрольної',
      then: 'потім',
      at: 'о {{time}}',
      alarmGo: 'Йди з учителем до укриття',
    },
    // the rows of what to do
    todo: {
      do: {
        homework: 'Здати',
        reading: 'Відкрити',
        quiz: 'Пройти',
        test: 'Деталі',
        rework: 'Переробити',
        ack: 'Підтвердити',
        catchUp: 'Наздогнати',
      },
      overdueSince: 'термін минув {{date}}',
      today: 'сьогодні',
      tomorrow: 'завтра',
      at: 'о {{time}}',
      quiz: 'Тест',
      announcement: 'Оголошення',
      more: 'ще {{count}}',
    },
  },
  // the student's own app speaks to them as «ти»: friendly, like a classmate, not like an office
  student: {
    notifications: 'Сповіщення',
    profile: 'Профіль',
    changeAvatar: 'Змінити звірятко',
    lessonsCount_one: '{{count}} урок',
    lessonsCount_few: '{{count}} уроки',
    lessonsCount_many: '{{count}} уроків',
    lessonsCount_other: '{{count}} уроку',
    today: 'сьогодні',
    on: {
      mon: 'у понеділок',
      tue: 'у вівторок',
      wed: 'у середу',
      thu: 'у четвер',
      fri: 'у пʼятницю',
      sat: 'у суботу',
      sun: 'у неділю',
    },
    until: 'до {{time}}',
    dayOff: 'Уроків немає — відпочивай!',
    stickers: {
      streak_one: 'день поспіль',
      streak_few: 'дні поспіль',
      streak_many: 'днів поспіль',
      streak_other: 'дня поспіль',
      streakA11y: 'Без пропусків: {{count}} днів поспіль',
      average: 'середній бал',
      homework: 'завдань',
      homeworkA11y: 'Завдань до здачі: {{count}}',
      achievements: 'досягнень',
      achievementsA11y: 'Досягнень: {{count}}',
      next: 'далі',
    },
    // labels under the student's own numbers on the home screen («11 завдань»)
    stats: {
      todo_one: 'завдання здати',
      todo_few: 'завдання здати',
      todo_many: 'завдань здати',
      todo_other: 'завдання здати',
      achievements_one: 'досягнення',
      achievements_few: 'досягнення',
      achievements_many: 'досягнень',
      achievements_other: 'досягнення',
    },
    lessons: {
      now: 'зараз',
      next: 'далі',
      over: 'Уроки на сьогодні закінчились',
      showAll: 'Усі уроки',
      collapse: 'Згорнути',
      join: 'Увійти',
      joinLesson: 'Увійти на урок',
      left: 'ще {{min}} хв',
      then: 'потім — {{subject}}, {{time}}',
      last: 'це останній урок',
    },
    todo: {
      title: 'Не забудь',
      overdue: 'прострочено!',
      today: 'на сьогодні',
      tomorrow: 'на завтра',
      allDone: 'Усе здано — так тримати!',
      more: 'Усі завдання',
    },
    grades: {
      title: 'Свіжі оцінки',
      count_one: '{{count}} оцінка',
      count_few: '{{count}} оцінки',
      count_many: '{{count}} оцінок',
      count_other: '{{count}} оцінки',
      all: 'Усі оцінки',
      none: 'За останні два тижні нових оцінок немає.',
    },
    news: {
      title: 'Оголошення',
      unread_one: '{{count}} нове',
      unread_few: '{{count}} нові',
      unread_many: '{{count}} нових',
      unread_other: '{{count}} нового',
      allRead: 'усе прочитано',
      read: 'Прочитано',
      ack: 'Ознайомився(-лась)',
      more: 'Ще оголошення',
    },
    life: {
      title: 'Шкільне життя',
      canteen: 'Їдальня',
      events: 'Події',
      quizzes: 'Тести',
      materials: 'Матеріали',
      achievements: 'Досягнення',
      album: 'Альбом',
      letters: 'Лист у майбутнє',
      lab: 'Лабораторія',
      clubs: 'Гуртки',
      tree: 'Дерево знань',
      nmt: 'НМТ',
      season: 'Сезон',
      yearbook: 'Альбом року',
      future: 'Майбутнє',
    },
    web: {
      hi: 'привіт!',
      todayYouHave: 'Сьогодні в тебе',
      groups: {
        study: 'навчання',
        talk: 'спілкування',
        life: 'шкільне життя',
      },
    },
    avatar: {
      title: 'Обери своє звірятко',
      hint: 'Звірятко бачиш у застосунку лише ти — фото не потрібне.',
    },
    schedule: {
      day: 'День',
      week: 'Тиждень',
      bigBreak: 'велика перерва',
      break: 'перерва · {{min}} хв',
      bigBreakFor: 'велика перерва · {{min}} хв',
      breakNow: 'зараз перерва · ще {{min}} хв',
      left: 'ще {{min}} хв',
      done: 'уроки закінчено о {{time}}',
      free: 'вихідний!',
      freeHint: 'Уроків немає. Можна відпочити або повторити щось цікаве.',
      now: 'зараз',
    },
    homework: {
      overline: 'домашка',
      title: 'Завдання',
      active_one: '{{count}} активне',
      active_few: '{{count}} активні',
      active_many: '{{count}} активних',
      active_other: '{{count}} активного',
      burning: 'Горить',
      folders: 'Предмети',
      tasks_one: '{{count}} завдання',
      tasks_few: '{{count}} завдання',
      tasks_many: '{{count}} завдань',
      tasks_other: '{{count}} завдання',
      nothing: 'нічого не задали',
      back: 'Усі предмети',
      open: 'Треба здати',
      done: 'Виконані',
      noOpen: 'Тут усе здано',
      answerPlaceholder: 'Напиши відповідь або коротко опиши, що зроблено',
      teacherSays: 'Учитель пише',
      due: 'до {{date}}',
    },
  },
  grading: {
    verbal: {
      needs_help: 'ще потребує допомоги',
      with_teacher_help: 'досягає результату з допомогою вчителя',
      notable_progress: 'демонструє помітний прогрес',
      significant_success: 'має значні успіхи',
    },
    verbalShort: {
      needs_help: 'ПД',
      with_teacher_help: 'ЗД',
      notable_progress: 'ПП',
      significant_success: 'ЗУ',
    },
    levels: {
      П: 'початковий',
      С: 'середній',
      Д: 'достатній',
      В: 'високий',
    },
    passed: 'зараховано',
    failed: 'не зараховано',
    notAssessed: 'н/а',
    exempt: 'звільнений',
  },
  counts: {
    students_one: '{{count}} учень',
    students_few: '{{count}} учні',
    students_many: '{{count}} учнів',
    students_other: '{{count}} учня',
    minutes_one: '{{count}} хвилина',
    minutes_few: '{{count}} хвилини',
    minutes_many: '{{count}} хвилин',
    minutes_other: '{{count}} хвилини',
  },
} as const;

export type Translation = typeof uk;
